import { Component, OnInit, ViewChild, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService, MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Toast } from 'primeng/toast';
import { AccordionModule } from 'primeng/accordion';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule, FileUpload } from 'primeng/fileupload';
import { DocumentsManagerComponent, DocManagerItem } from '../../documents-manager/documents-manager.component';
import {
  CollapsibleSectionDirective,
  SectionCollapseToggleComponent,
} from '../../../../core/shared/collapsible-section/collapsible-section';
import { MessageModule } from 'primeng/message';
import {
  ContractAdministration,
  ContractDataProcessing,
  ContractEconomics,
  ContractFrameworkAgreement,
  ContractGeneralInfo,
  ContractInvolvedEntity,
  ContractRequest,
  ContractResponse,
  RelatedDocument,
  DocumentType,
} from '../../../models/contract.model';
import { ProfessionalSummaryResponse } from '../../../../core/models/professional.models';
import { Supplier } from '../../../models/supplier.model';
import { SupplierRegistryService } from '../../../services/supplier-registry.service';
import { AttoDiNominaResponse, AttoDiNominaResponsible, SubResponsible } from '../../../models/atto-di-nomina.model';
import { ContractRegistryService } from '../../../services/contract-registry.service';
import {
  ContractAction,
  ContractActionType,
  ContractActionStatus,
  CONTRACT_ACTION_TYPE_LABELS,
  CONTRACT_ACTION_STATUS_LABELS,
  CONTRACT_ACTION_TYPE_OPTIONS,
  getActionStatusSeverity,
} from '../../../models/contract-action.model';
import { ContractActionService } from '../../../services/contract-action.service';
import { DocumentTemplateV2Service } from '../../../services/document-template-v2.service';
import { DocumentAssetSummary } from '../../../models/document-asset.model';
import { isSelectableTemplate, toTemplateSelectionRow } from '../../../models/document-asset-labels';
import {
  FormDropdownService,
  FormDropdownItem,
} from '../../../../core/services/form-dropdown.service';
import { AxaEntity, AxaEntityService } from '../../../../core/services/axa-entity.service';
import { GeographyService } from '../../../../core/services/geography.service';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { AuthorizationService } from '../../../../core/services/authorization.service';
import { NavigationContextService } from '../../../../core/services/navigation-context.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { RegistrationStepService, RegistrationStep } from '../../../../core/services/registration-step.service';
import { AppRole } from '../../../../core/models/permissions.model';
import { ResponsibleRegistryService } from '../../../services/responsible-registry.service';
import { SubResponsibleRegistryService } from '../../../services/sub-responsible-registry.service';
import { RegistryTableComponent } from '../../../../core/registry-table/registry-table.component';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { ColumnConfig, TABLE_CONFIG } from '../../dashboard/table-config';
import { forkJoin, of, catchError, map, finalize, timeout, TimeoutError, Observable, switchMap } from 'rxjs';
import { NoSpecialCharsValidator, escapeHtml } from '../../../../core/validators/no-special-chars.validator';
import { hasMeaningfulText } from '../../../../core/validators/text-value.utils';
import { addSolCodeToFileName } from '../../../../core/utils/file-name.utils';

@Component({
  selector: 'app-contract-detail',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    InputNumber,
    Toast,
    BreadcrumbModule,
    AccordionModule,
    TableModule,
    TooltipModule,
    RadioButtonModule,
    DialogModule,
    FileUploadModule,
    MessageModule,
    RegistryTableComponent,
    DocumentsManagerComponent,
    CollapsibleSectionDirective,
    SectionCollapseToggleComponent,
  ],
  templateUrl: './contract-detail.component.html',
  styleUrls: ['./contract-detail.component.scss'],
})
export class ContractDetailComponent implements OnInit {
  @ViewChild('mainDocUpload') mainDocUpload?: FileUpload;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private contractRegistryService = inject(ContractRegistryService);
  private documentTemplateV2Service = inject(DocumentTemplateV2Service);
  private formDropdownService = inject(FormDropdownService);
  private axaEntityService = inject(AxaEntityService);
  private geographyService = inject(GeographyService);
  private authzService = inject(AuthorizationService);
  private navigationContext = inject(NavigationContextService);
  private professionalsService = inject(ProfessionalsService);
  private supplierRegistryService = inject(SupplierRegistryService);
  private registrationStepService = inject(RegistrationStepService);
  private responsibleRegistryService = inject(ResponsibleRegistryService);
  private subResponsibleRegistryService = inject(SubResponsibleRegistryService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private fileValidationService = inject(FileValidationService);
  private contractActionService = inject(ContractActionService);

  isEditModeGenerali = signal(false);
  isEditModeQuadro = signal(false);
  isEditModeEconomics = signal(false);
  isEditModeAmministrazione = signal(false);
  isEditModeVRF = signal(false);
  isEditModeTemplates = signal(false);

  // Atto di Nomina - sezioni separate (una per ogni sezione del form)
  isEditModeTitolari = signal(false);
  isEditModeResponsabile = signal(false);
  isEditModeSubResponsabili = signal(false);
  isEditModeOperazioniTrattamento = signal(false);
  isEditModeFinalitaTrattamento = signal(false);
  isEditModeCategorieSoggetti = signal(false);
  isEditModeDestinatari = signal(false);
  isEditModeConservazioneCancellazione = signal(false);
  isEditModeStrumentiUtilizzati = signal(false);
  isEditModeMisureSicurezza = signal(false);
  isEditModeAmministratoriSistema = signal(false);
  isEditModeInformazioniCloud = signal(false);
  adminsTextValidationError = signal<string | null>(null);
  private readonly hasMeaningfulText = hasMeaningfulText;

  // Computed signal: restituisce true se almeno una sezione (contratto o atto di nomina) è in edit mode
  hasAnySectionInEditMode = computed(
    () =>
      this.isEditModeGenerali() ||
      this.isEditModeQuadro() ||
      this.isEditModeEconomics() ||
      this.isEditModeAmministrazione() ||
      this.isEditModeVRF() ||
      this.isEditModeTemplates() ||
      this.isEditModeTitolari() ||
      this.isEditModeResponsabile() ||
      this.isEditModeSubResponsabili() ||
      this.isEditModeOperazioniTrattamento() ||
      this.isEditModeFinalitaTrattamento() ||
      this.isEditModeCategorieSoggetti() ||
      this.isEditModeDestinatari() ||
      this.isEditModeConservazioneCancellazione() ||
      this.isEditModeStrumentiUtilizzati() ||
      this.isEditModeMisureSicurezza() ||
      this.isEditModeAmministratoriSistema() ||
      this.isEditModeInformazioniCloud(),
  );

  isLoading = signal(true);
  breadcrumbItems = signal<MenuItem[]>([]);
  contractId = signal<string | undefined>(undefined);
  currentProfessionalStep = signal<number | null>(null); // Professional registration step as number (1-4)
  documents = signal<RelatedDocument[]>([]);
  /** Documenti mappati per il componente riutilizzabile documents-manager. */
  contractDocItems = computed<DocManagerItem[]>(() =>
    this.documents().map(d => ({
      id: d.documentId,
      fileName: d.fileName,
      title: d.title,
      uploadDate: d.uploadDate ? new Date(d.uploadDate).toLocaleDateString('it-IT') : null,
      fileSize: null,
      raw: d,
    }))
  );

  // Contract data from BE
  contract = signal<ContractResponse | null>(null);
  professionalData = signal<ProfessionalSummaryResponse | null>(null);
  supplierData = signal<Supplier | null>(null);
  supplierTypeDescription = signal<string>('');
  // Atto di nomina data from BE (when gdprDataProcessing = true)
  attoDiNomina = signal<AttoDiNominaResponse | null>({
    id: 0,
    contractId: '',
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    updatedBy: '',
    controllers: [],
    responsible: {
      responsibleBusinessName: '',
      companyType: '',
      headquarters: '',
      processedDataTypes: [],
      processDescription: '',
      responsibleType: '',
      dataProcessingLocation: '',
      externalSysAdminsText: '',
      responsibleDpoEmail: '',
      transferOutsideEu: false,
      dataDeletionProgram: false,
      criticalSupplierQualification: '',
      usesSubResponsibles: false,
      subResponsibles: []
    } as AttoDiNominaResponsible,
    activityDescription: [],
    mainPurpose: '',
    category: [],
    recipientCategory: [],
    retentionPeriod: 0,
    deletionMethod: '',
    otherTools: '',
    measure: '',
    adminsText: ''
  } as AttoDiNominaResponse);
  // Backup per ripristino su annulla
  private attoDiNominaBackup: AttoDiNominaResponse | null = null;

  // Backup amministrazione per ripristino su annulla
  private administrationBackup: any = null;

  // Backup sezioni contratto per ripristino su annulla
  private generalInfoBackup: ContractResponse['generalInfo'] | null = null;
  private frameworkAgreementBackup: ContractResponse['frameworkAgreement'] | null = null;
  private economicsBackup: ContractResponse['economics'] | null = null;
  private dataProcessingBackup: ContractResponse['dataProcessing'] | null = null;

  // Main Document
  mainDocumentUrl = signal<string | null>(null);
  isUploadingMainDocument = signal(false);
  uploadErroredMainDoc = signal(false);
  /** True quando il BO vuole sostituire il documento controfirmato già caricato (step 3) */
  isReplacingFinalDoc = signal(false);

  // Stato di caricamento allegato (usato dal componente documents-manager)
  isUploadingAttachment = signal(false);

  // Regenerate Contract Document
  isRegeneratingDocument = signal(false);

  // Computed: determina se è possibile rigenerare il contratto
  canRegenerateContract = computed(() => {
    const status = this.contract()?.generalInfo?.status;
    const templateId = this.contract()?.generalInfo?.contractTemplateId;
    return status === 'GENERATED' && !!templateId;
  });

  // Computed: dati del fiduciario (solo per contratti professional)
  professionalId = computed(() => {
    return this.contract()?.generalInfo?.professionalId || '';
  });

  professionalBusinessName = computed(() => {
    const professional = this.professionalData();
    if (!professional) return '';

    return (
      professional.businessName ||
      [professional.lastName, professional.firstName].filter(Boolean).join(' ')
    );
  });

  professionalCategory = computed(() => {
    return this.getDropdownLabel(this.trusteeCategoryOptions(), this.professionalData()?.type);
  });

  // Computed: dati del fornitore (solo per contratti supplier)
  supplierId = computed(() => {
    if (this.isContractProfessional()) return '';
    const id = this.contract()?.generalInfo?.supplierId;
    return id != null ? String(id) : '';
  });

  supplierDisplayName = computed(() => {
    const supplierName = this.supplierData()?.supplierName?.trim() || '';
    if (!supplierName) return '';

    // Usa solo la sigla tra parentesi della descrizione tipo società (es. "Società a responsabilità limitata (SRL)" -> "SRL")
    const companyTypeSuffix =
      this.supplierTypeDescription().match(/\(([^()]+)\)\s*$/)?.[1]?.trim() || '';

    return [supplierName, companyTypeSuffix].filter(Boolean).join(' ');
  });

  // Computed: tooltip per pulsante Rigenera Contratto
  regenerateContractTooltip = computed(() => {
    if (this.isRegeneratingDocument()) {
      return 'Rigenerazione in corso...';
    }
    if (!this.canRegenerateContract()) {
      return 'Il contratto deve essere nello stato GENERATED e avere un template associato';
    }
    return 'Rigenera il documento del contratto dal template';
  });

  // Computed: determina se il contratto è editabile (solo se stato === 'GENERATED' E se professional, solo se step < 2)
  isContractEditable = computed(() => {
    const status = this.contract()?.generalInfo?.status;
    if (status !== 'GENERATED') {
      return false;
    }
    // Se professional e già inviato al fiduciario (step >= 2), blocca modifiche
    const step = this.professionalStep();
    if (this.isContractProfessional() && step !== null && step >= 2) {
      return false;
    }
    return true;
  });

  // Computed: determina se la sezione amministrazione è editabile (GENERATED o ACTIVE, ma professional solo se step < 2)
  isAdministrationEditable = computed(() => {
    const status = this.contract()?.generalInfo?.status;
    if (status !== 'GENERATED' && status !== 'ACTIVE') {
      return false;
    }
    // Se professional e già inviato al fiduciario (step >= 2), blocca modifiche
    const step = this.professionalStep();
    if (this.isContractProfessional() && step !== null && step >= 2) {
      return false;
    }
    return true;
  });

  // Computed: tooltip per pulsante Attiva Contratto
  activateContractTooltip = computed(() => {
    if (!this.mainDocumentUrl()) {
      return 'Carica il documento principale del contratto prima di attivarlo';
    }
    return 'Attiva il contratto per renderlo operativo';
  });

  // ==================== PROFESSIONAL WORKFLOW COMPUTED ====================

  // Current professional step from registration-step service
  professionalStep = computed(() => {
    return this.currentProfessionalStep();
  });

  // Workflow status labels
  professionalStepLabel = computed(() => {
    const step = this.professionalStep();
    if (!this.isContractProfessional()) return '';
    
    switch (step) {
      case null:
      case 1:
        return 'Documento da caricare';
      case 2:
        return 'In attesa firma fiduciario';
      case 3:
        return 'Controfirma richiesta';
      case 4:
        return 'Contratto attivo';
      default:
        return 'Stato sconosciuto';
    }
  });

  // Show upload section: professional contract in step 1 (CONTRACT_SENT)
  isInUploadStep = computed(() => {
    const step = this.professionalStep();
    return this.isContractProfessional() && step === 1;
  });

  // Can send to fiduciary: step 1 + document uploaded
  canSendToFiduciary = computed(() => {
    return this.isInUploadStep() && !!this.mainDocumentUrl();
  });

  // Is waiting for fiduciary signature: step 2
  isWaitingForFiduciarySignature = computed(() => {
    return this.isContractProfessional() && this.professionalStep() === 2;
  });

  // Can upload countersigned document: step 3
  canCountersignDocument = computed(() => {
    return this.isContractProfessional() && this.professionalStep() === 3;
  });

  // Can activate professional contract: step 3 + countersigned document uploaded (s3FileUrl = FINAL)
  canActivateProfessionalContract = computed(() => {
    const contract = this.contract();
    return this.canCountersignDocument() && !!contract?.mainDocument?.s3FileUrl;
  });

  // Professional contract is active: step 4
  isProfessionalContractActive = computed(() => {
    return this.isContractProfessional() && this.professionalStep() === 4;
  });

  // Available documents for download
  hasUnsignedDocument = computed(() => !!this.contract()?.mainDocument?.s3FileUrlUnsigned);
  hasExternallySignedDocument = computed(() => !!this.contract()?.mainDocument?.s3FileUrlExternallySigned);
  hasFinalDocument = computed(() => !!this.contract()?.mainDocument?.s3FileUrl);
  
  // Show documents section when:
  // - Professional contracts: step >= 3 and at least one document available
  // - Supplier contracts: always show if FINAL document exists
  shouldShowDocumentsSection = computed(() => {
    const isProfessional = this.isContractProfessional();
    const step = this.professionalStep();
    
    if (isProfessional) {
      // Professional: show from step 3 onwards if documents exist
      return step !== null && step >= 3 && 
             (this.hasUnsignedDocument() || this.hasExternallySignedDocument() || this.hasFinalDocument());
    } else {
      // Supplier: show if FINAL document exists
      return this.hasFinalDocument();
    }
  });

  // Computed: determina se mostrare la sezione azioni (solo se contratto != GENERATED)
  shouldShowActionsSection = computed(() => {
    const status = this.contract()?.generalInfo?.status;
    return status !== 'GENERATED';
  });

  // Computed: determina se è possibile creare nuove azioni o modificare quelle in DRAFT (solo se ACTIVE o IN_RENEWAL)
  canCreateNewActions = computed(() => {
    const status = this.contract()?.generalInfo?.status;
    return status === 'ACTIVE' || status === 'IN_RENEWAL';
  });

  // Computed: tooltip per pulsante Nuova Azione
  newActionTooltip = computed(() => {
    if (!this.canCreateNewActions()) {
      // Get decoded labels for ACTIVE and IN_RENEWAL
      const activeLabel =
        this.statusOptions().find((opt) => opt.valueKey === 'ACTIVE')?.valueDescription || 'Attivo';
      const renewalLabel =
        this.statusOptions().find((opt) => opt.valueKey === 'IN_RENEWAL')?.valueDescription ||
        'In Rinnovo';
      return `È possibile creare nuove azioni solo se il contratto è nello stato ${activeLabel} o ${renewalLabel}`;
    }
    return 'Aggiungi una nuova azione al contratto';
  });

  // Dropdown options (loaded from API)
  contractTypeOptions = signal<FormDropdownItem[]>([]);
  serviceTypeOptions = signal<FormDropdownItem[]>([]);
  externalizedActivityOptions = signal<FormDropdownItem[]>([]);
  renewalNoticeOptions = signal<FormDropdownItem[]>([]);
  statusOptions = signal<FormDropdownItem[]>([]);
  entityOptions = signal<AxaEntity[]>([]);
  trusteeCategoryOptions = signal<FormDropdownItem[]>([]);
  synergiesCompaniesOptions = signal<FormDropdownItem[]>([]);
  toolGestionaliOptions = signal<FormDropdownItem[]>([]);
  networkOptions = signal<FormDropdownItem[]>([]);
  supplierTypeOptions = signal<FormDropdownItem[]>([]);
  currencyOptions = signal<FormDropdownItem[]>([]);
  invoiceFrequencyOptions = signal<FormDropdownItem[]>([]);
  claimsAreaOptions = signal<FormDropdownItem[]>([]);
  vrfUseCaseOptions = signal<FormDropdownItem[]>([]);
  automaticRenewalOptions = signal<FormDropdownItem[]>([]);
  managingAreaOptions = signal<FormDropdownItem[]>([]);
  involvedStructuresOptions = signal<FormDropdownItem[]>([]);
  distributionTypeOptions = signal<FormDropdownItem[]>([]);
  ownershipTypeOptions = signal<FormDropdownItem[]>([]);
  countriesOptions = signal<FormDropdownItem[]>([]);

  // Atto di Nomina dropdown options
  processedDataTypesOptions = signal<FormDropdownItem[]>([]);
  referenceControllerOptions = signal<FormDropdownItem[]>([]);
  responsibleTypeOptions = signal<FormDropdownItem[]>([]);
  criticalSupplierQualificationOptions = signal<FormDropdownItem[]>([]);
  subDataProcessingLocationOptions = signal<FormDropdownItem[]>([]);
  subjectCategoryOptions = signal<FormDropdownItem[]>([]);
  recipientCategoryOptions = signal<FormDropdownItem[]>([]);
  nonEuCountriesOptions = signal<FormDropdownItem[]>([]);
  activityDescriptionOptions = signal<FormDropdownItem[]>([]);
  mainPurposeOptions = signal<FormDropdownItem[]>([]);
  deletionMethodOptions = signal<FormDropdownItem[]>([]);

  // Template options
  templateContrattoOptions = signal<Array<{ label: string; value: string }>>([]);
  templateAttoNominaOptions = signal<Array<{ label: string; value: string }>>([]);
  templateContrattoTableData = signal<any[]>([]);
  templateAttoNominaTableData = signal<any[]>([]);
  showTemplateContrattoDialog = signal<boolean>(false);
  showTemplateAttoNominaDialog = signal<boolean>(false);
  templateContrattoColumns: ColumnConfig[] = [];

  // Contract Actions
  contractActions = signal<ContractAction[]>([]);

  templateAttoNominaColumns: ColumnConfig[] = [];
  private templatesBackup: {
    contractTemplateId: string | null;
    attoDiNominaTemplateId: string | null;
  } | null = null;

  // Computed per template selezionati
  selectedContractTemplate = computed(() => {
    const contract = this.contract();
    const templateId = contract?.generalInfo?.contractTemplateId;
    if (!templateId) return null;
    const template = this.templateContrattoOptions().find((t) => t.value === templateId);
    return template || null;
  });

  selectedAttoNominaTemplate = computed(() => {
    const attoNomina = this.attoDiNomina();
    const templateId = attoNomina?.attoDiNominaTemplateId;
    if (!templateId) return null;
    const template = this.templateAttoNominaOptions().find((t) => t.value === templateId);
    return template || null;
  });

  // Check if user is backoffice professional
  isBackofficeProfessional = computed(() => {
    const roles = this.authzService.currentUser()?.roles ?? [];
    return (
      roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) ||
      roles.includes('backoffice_professional' as AppRole) ||
      roles.includes('backoffice_professionals' as AppRole)
    );
  });

  // Check if the loaded contract is for a professional (not a supplier)
  isContractProfessional = computed(() => {
    const professionalId = this.contract()?.generalInfo?.professionalId;
    return professionalId != null && professionalId !== '';
  });

  isSelectedEntityProfessional = computed(() => this.isContractProfessional());

  // Computed per verificare se mostrare la sezione atto di nomina
  // Always show for backoffice professionals (gdprDataProcessing field is hidden)
  shouldShowAttoDiNominaTemplate = computed(() => {
    if (this.isBackofficeProfessional()) {
      return true;
    }
    return this.contract()?.dataProcessing?.gdprDataProcessing === true;
  });

  // Registry dialog signals
  showResponsibleDialog = signal(false);
  showSubResponsibleDialog = signal(false);
  loadingResponsibles = signal(false);
  loadingSubResponsibles = signal(false);
  responsibleTableData = signal<any[]>([]);
  subResponsibleTableData = signal<any[]>([]);
  selectedSubResponsibleIndex: number | null = null;

  // Registry table columns
  responsibleColumns = [
    {
      title: 'Ragione Sociale',
      field: 'businessName',
      sortable: true,
      filterType: 'text' as const,
    },
    { title: 'Sede', field: 'headquarters', sortable: true, filterType: 'text' as const },
    { title: 'Email', field: 'email', sortable: true, filterType: 'text' as const },
    { title: 'Azioni', type: 'action' as const },
  ];

  subResponsibleColumns = [
    {
      title: 'Ragione Sociale',
      field: 'businessName',
      sortable: true,
      filterType: 'text' as const,
    },
    { title: 'Sede', field: 'headquarters', sortable: true, filterType: 'text' as const },
    { title: 'Email', field: 'email', sortable: true, filterType: 'text' as const },
    { title: 'Azioni', type: 'action' as const },
  ];

  ngOnInit(): void {
    this.titleService.setTitle('Dettaglio - PalEasy');

    // Load dropdowns first
    this.loadAllDropdowns();

    // Load table columns for template dialogs
    this.loadTemplateColumns();

    // Recupera lo state dalla navigazione per costruire il breadcrumb dinamico
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    this.route.params.subscribe((params) => {
      console.log('Route params:', params);
      const id = params['id'];
      console.log('Extracted id:', id, 'Type:', typeof id);

      if (id) {
        this.contractId.set(id);

        // Imposta subito il breadcrumb con l'ID
        this.breadcrumbItems.set([
          { label: 'Registro Contratti', routerLink: `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry` },
          { label: `Contratto ${id}` },
        ]);

        this.loadContractDetails(id);
        this.loadTemplateOptions();
        this.loadContractActions();
      } else {
        console.warn('No id found in route params');
        this.isLoading.set(false);
      }
    });
  }

  private loadAllDropdowns(): void {
    // Execute all dropdown loading calls in parallel
    const dropdownsObservable = forkJoin({
      tipoContratto: this.formDropdownService
        .getFormDropdownValues(
          'CONTRACT_FORM',
          this.navigationContext.isSupplierContext()
            ? 'SUPPLIER_CONTRACT_TYPE'
            : 'CONTRACT_TYPE',
        )
        .pipe(catchError(() => of({ status: false, data: [] }))),
      serviceTypes: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'SERVICE_TYPE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      attivitaEster: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'OUTSOURCED_ACTIVITY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      preavvisoRinnovo: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'RENEWAL_NOTICE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      status: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'CONTRACT_STATUS')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      entity: this.axaEntityService
        .getAll()
        .pipe(catchError(() => of({ status: false, data: [] }))),
      trusteeCategories: this.formDropdownService
        .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      sinergieCompagnie: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'SYNERGY_COMPANY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      tipoFornitore: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'SUPPLIER_TYPE_CONTRACT')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      currency: this.formDropdownService
        .getFormDropdownValues('CURRENCY', 'CURRENCY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      periodicitaFattura: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'INVOICE_FREQUENCY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      areaClaims: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'CLAIMS_AREA')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      casoUsoVRF: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'VRF_USE_CASE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      rinnovoTacito: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'TACIT_RENEWAL')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      gestioneInCarico: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'MANAGEMENT_IN_CHARGE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      struttureCoinvolte: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'INVOLVED_STRUCTURES')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      tipologiaRipartizione: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'DISTRIBUTION_TYPE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      tipoTitolarita: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'OWNERSHIP_TYPE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      toolGestionali: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'MANAGEMENT_TOOLS')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      network: this.formDropdownService
        .getFormDropdownValues('CONTRACT_FORM', 'NETWORK')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      luogoTrattamento: this.geographyService.getCountries().pipe(
        map((countries) =>
          countries.map((country) => ({
            formId: 'GEO',
            fieldId: 'COUNTRY',
            valueKey: country.code,
            valueDescription: country.name,
          })),
        ),
        catchError(() => of([])),
      ),
      // Atto di Nomina dropdowns
      processedDataTypes: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'PROCESSED_DATA_TYPES')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      referenceController: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'REFERENCE_CONTROLLER_ENTITIES')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      responsibleType: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'RESPONSIBLE_TYPE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      criticalSupplierQualification: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'CRITICAL_SUPPLIER_QUALIFICATION')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      subDataProcessingLocation: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'SUB_DATA_PROCESSING_LOCATION')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      subjectCategory: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'SUBJECT_CATEGORY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      recipientCategory: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'RECIPIENT_CATEGORY')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      activityDescription: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'ACTIVITY_DESCRIPTION')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      mainPurpose: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'MAIN_PURPOSE')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      deletionMethod: this.formDropdownService
        .getFormDropdownValues('ATTO_DI_NOMINA', 'DELETION_METHOD')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      vrfDeletionMethod: this.formDropdownService
        .getFormDropdownValues('VRF_QUESTIONNAIRE', 'CANCELLATION_METHOD')
        .pipe(catchError(() => of({ status: false, data: [] }))),
      nonEuCountries: this.geographyService.getCountries(false).pipe(
        map((countries) =>
          countries.map((country) => ({
            formId: 'GEO',
            fieldId: 'NON_EU_COUNTRY',
            valueKey: country.code,
            valueDescription: country.name,
          })),
        ),
        catchError(() => of([])),
      ),
    });

    dropdownsObservable
      .pipe(
        timeout(30000),
        catchError((err) => {
          console.error('Error loading dropdowns:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento delle opzioni ha impiegato troppo tempo.'
              : 'Impossibile caricare alcune opzioni.',
            life: 5000,
          });
          return of({} as any);
        }),
      )
      .subscribe({
        next: (results) => {
          // Set all dropdown options
          if (results.tipoContratto?.status && results.tipoContratto?.data) {
            this.contractTypeOptions.set(results.tipoContratto.data);
          }
          if (results.serviceTypes?.status && results.serviceTypes?.data) {
            this.serviceTypeOptions.set(results.serviceTypes.data);
          }
          if (results.attivitaEster?.status && results.attivitaEster?.data) {
            this.externalizedActivityOptions.set(results.attivitaEster.data);
          }
          if (results.preavvisoRinnovo?.status && results.preavvisoRinnovo?.data) {
            this.renewalNoticeOptions.set(results.preavvisoRinnovo.data);
          }
          if (results.status?.status && results.status?.data) {
            this.statusOptions.set(results.status.data);
          }
          if (results.entity?.status && results.entity?.data) {
            this.entityOptions.set(results.entity.data);
          }
          if (results.trusteeCategories?.status && results.trusteeCategories?.data) {
            this.trusteeCategoryOptions.set(results.trusteeCategories.data);
          }
          if (results.sinergieCompagnie?.status && results.sinergieCompagnie?.data) {
            this.synergiesCompaniesOptions.set(results.sinergieCompagnie.data);
          }
          if (results.toolGestionali?.status && results.toolGestionali?.data) {
            this.toolGestionaliOptions.set(results.toolGestionali.data);
          }
          if (results.network?.status && results.network?.data) {
            this.networkOptions.set(results.network.data);
          }
          if (results.tipoFornitore?.status && results.tipoFornitore?.data) {
            this.supplierTypeOptions.set(results.tipoFornitore.data);
          }
          if (results.currency?.status && results.currency?.data) {
            this.currencyOptions.set(results.currency.data);
          }
          if (results.periodicitaFattura?.status && results.periodicitaFattura?.data) {
            this.invoiceFrequencyOptions.set(results.periodicitaFattura.data);
          }
          if (results.areaClaims?.status && results.areaClaims?.data) {
            this.claimsAreaOptions.set(results.areaClaims.data);
          }
          if (results.casoUsoVRF?.status && results.casoUsoVRF?.data) {
            this.vrfUseCaseOptions.set(results.casoUsoVRF.data);
          }
          if (results.rinnovoTacito?.status && results.rinnovoTacito?.data) {
            this.automaticRenewalOptions.set(results.rinnovoTacito.data);
          }
          if (results.gestioneInCarico?.status && results.gestioneInCarico?.data) {
            this.managingAreaOptions.set(results.gestioneInCarico.data);
          }
          if (results.struttureCoinvolte?.status && results.struttureCoinvolte?.data) {
            this.involvedStructuresOptions.set(results.struttureCoinvolte.data);
          }
          if (results.tipologiaRipartizione?.status && results.tipologiaRipartizione?.data) {
            this.distributionTypeOptions.set(results.tipologiaRipartizione.data);
          }
          if (results.tipoTitolarita?.status && results.tipoTitolarita?.data) {
            this.ownershipTypeOptions.set(results.tipoTitolarita.data);
          }
          if (results.luogoTrattamento) {
            this.countriesOptions.set(results.luogoTrattamento);
          }

          // Atto di Nomina dropdowns
          if (results.processedDataTypes?.status && results.processedDataTypes?.data) {
            this.processedDataTypesOptions.set(results.processedDataTypes.data);
          }
          if (results.referenceController?.status && results.referenceController?.data) {
            this.referenceControllerOptions.set(results.referenceController.data);
          }
          if (results.responsibleType?.status && results.responsibleType?.data) {
            this.responsibleTypeOptions.set(results.responsibleType.data);
          }
          if (
            results.criticalSupplierQualification?.status &&
            results.criticalSupplierQualification?.data
          ) {
            this.criticalSupplierQualificationOptions.set(
              results.criticalSupplierQualification.data,
            );
          }
          if (
            results.subDataProcessingLocation?.status &&
            results.subDataProcessingLocation?.data
          ) {
            this.subDataProcessingLocationOptions.set(results.subDataProcessingLocation.data);
          }
          if (results.subjectCategory?.status && results.subjectCategory?.data) {
            this.subjectCategoryOptions.set(results.subjectCategory.data);
          }
          if (results.recipientCategory?.status && results.recipientCategory?.data) {
            this.recipientCategoryOptions.set(results.recipientCategory.data);
          }
          if (results.activityDescription?.status && results.activityDescription?.data) {
            this.activityDescriptionOptions.set(results.activityDescription.data);
          }
          if (results.mainPurpose?.status && results.mainPurpose?.data) {
            this.mainPurposeOptions.set(results.mainPurpose.data);
          }
          this.deletionMethodOptions.set([
            ...(results.deletionMethod?.status ? results.deletionMethod.data ?? [] : []),
            ...(results.vrfDeletionMethod?.status ? results.vrfDeletionMethod.data ?? [] : []),
          ]);
          if (results.nonEuCountries) {
            this.nonEuCountriesOptions.set(results.nonEuCountries);
          }
        },
      });
  }

  loadContractDetails(id: string, silent = false): void {
    // silent=true: ricarica in-place dopo un'azione (upload/attivazione). Non attiva lo spinner
    // full-page, così il contenuto non viene smontato e la posizione di scroll resta invariata.
    if (!silent) {
      this.isLoading.set(true);
    }

    this.contractRegistryService
      .getContractById(id)
      .pipe(
        timeout(15000),
        catchError((err) => {
          console.error('Error loading contract:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento del contratto ha impiegato troppo tempo.'
              : 'Impossibile caricare il contratto.',
            life: 5000,
          });
          this.isLoading.set(false);
          return of(null);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response && response.status && response.data) {
            const contract = this.normalizeContractForView(response.data);

            this.contract.set(contract);

            if (contract.generalInfo?.professionalId) {
              this.loadProfessionalData(contract.generalInfo.professionalId);
            } else if (contract.generalInfo?.supplierId != null) {
              this.loadSupplierData(String(contract.generalInfo.supplierId));
            }

            // Set documents from relatedDocuments or attachments
            let documents: RelatedDocument[] = [];

            // Legacy: relatedDocuments (se esistono)
            if (contract.relatedDocuments && contract.relatedDocuments.length > 0) {
              documents = contract.relatedDocuments;
            }
            // New: attachments (mappa a RelatedDocument)
            else if (contract.attachments && contract.attachments.length > 0) {
              documents = contract.attachments.map((att) => ({
                documentId: att.id,
                fileName: att.fileName,
                uploadDate: att.uploadedAt,
                uploadedBy: att.uploadedBy,
                title: att.description,
                documentS3: att.s3FileUrl,
              }));
            }

            this.documents.set(documents);

            // Set main document URL if exists
            // Per contratti professional, l'URL corretto verrà settato dopo loadProfessionalStep()
            // Per contratti normali, usa sempre FINAL
            if (contract.mainDocument && !this.isContractProfessional()) {
              const documentUrl = contract.mainDocument.s3FileUrl || null;
              if (documentUrl) {
                this.mainDocumentUrl.set(documentUrl);
                this.uploadErroredMainDoc.set(false);
              }
            }

            // Se professional, carica lo step corrente dalla registration-step API
            // Questo chiamerà updateMainDocumentUrlForStep() per settare l'URL corretto
            if (this.isContractProfessional() && contract.generalInfo?.professionalId) {
              this.loadProfessionalStep(contract.generalInfo.professionalId);
            }

            // Se professional O gdprDataProcessing = true, carica anche l'atto di nomina
            if (this.isContractProfessional() || contract.dataProcessing?.gdprDataProcessing) {
              // Update contractId in existing placeholder
              this.attoDiNomina.update(atto => ({ ...atto!, contractId: id }));
              
              this.loadAttoDiNomina(id);
            } else {
              this.isLoading.set(false);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Contratto non trovato.',
              life: 5000,
            });
            this.isLoading.set(false);
          }
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  private loadProfessionalData(professionalId: string): void {
    this.professionalsService
      .getProfessionals(0, 1, undefined, { id: professionalId })
      .pipe(
        timeout(15000),
        catchError((err) => {
          console.error('Error loading professional data:', err);
          return of(null);
        }),
      )
      .subscribe((response) => {
        const professional = response?.status ? response.data?.content?.[0] : undefined;
        this.professionalData.set(professional ?? null);
      });
  }

  private loadSupplierData(supplierId: string): void {
    this.supplierRegistryService
      .getSupplier(supplierId)
      .pipe(
        timeout(15000),
        map((response: any) => (response?.data ?? response) as Supplier | null),
        switchMap((supplier) =>
          this.formDropdownService.decodeSupplierType(supplier?.supplierTypeId).pipe(
            catchError(() => of('')),
            map((typeDescription) => ({ supplier, typeDescription })),
          ),
        ),
        catchError((err) => {
          console.error('Error loading supplier data:', err);
          return of({ supplier: null, typeDescription: '' });
        }),
      )
      .subscribe(({ supplier, typeDescription }) => {
        this.supplierData.set(supplier ?? null);
        this.supplierTypeDescription.set(typeDescription || '');
      });
  }

  /**
   * Carica lo step corrente del professional dalla registration-step API
   * E lo mappa a numero (1-4) per il workflow
   */
  private loadProfessionalStep(professionalId: string): void {
    this.registrationStepService.getCurrentStep(professionalId).subscribe({
      next: (stepResponse) => {
        const stepNumber = this.mapRegistrationStepToNumber(stepResponse.step);
        this.currentProfessionalStep.set(stepNumber);
        console.log(`[Professional Step] ${stepResponse.step} -> ${stepNumber}`);
        
        // Aggiorna l'URL del documento in base allo step appena caricato
        this.updateMainDocumentUrlForStep();
      },
      error: (err) => {
        console.error('Error loading professional step:', err);
        // Se non riesce a caricare lo step, assume step 1 (CONTRACT_SENT)
        this.currentProfessionalStep.set(1);
        this.updateMainDocumentUrlForStep();
      },
    });
  }

  /**
   * Mappa RegistrationStep enum a numero per professionalStep
   * CONTRACT_SENT (4) -> 1
   * CONTRACT_SIGNATURE (5) -> 2
   * CONTRACT_COUNTERSIGNATURE (6) -> 3
   * COMPLETED (professional ACTIVE) -> 4
   */
  private mapRegistrationStepToNumber(step: RegistrationStep): number {
    switch (step) {
      case RegistrationStep.CONTRACT_SENT:
        return 1;
      case RegistrationStep.CONTRACT_SIGNATURE:
        return 2;
      case RegistrationStep.CONTRACT_COUNTERSIGNATURE:
        return 3;
      default:
        // Se ha raggiunto COMPLETED o step successivi, considera attivo (4)
        return 4;
    }
  }

  /**
   * Aggiorna mainDocumentUrl in base allo step professionale corrente
   * Sceglie tra UNSIGNED, EXTERNALLY_SIGNED o FINAL
   */
  private updateMainDocumentUrlForStep(): void {
    const contract = this.contract();
    if (!contract?.mainDocument) {
      return;
    }

    const step = this.professionalStep();
    let documentUrl = null;

    if (this.isContractProfessional()) {
      // Step 1-2: mostra UNSIGNED (documento da firmare/in attesa firma)
      if (step === 1 || step === 2) {
        documentUrl = contract.mainDocument.s3FileUrlUnsigned || null;
      }
      // Step 3: mostra EXTERNALLY_SIGNED (documento firmato dal fiduciario)
      else if (step === 3) {
        documentUrl = contract.mainDocument.s3FileUrlExternallySigned || null;
      }
      // Step 4 o completato: mostra FINAL (documento controfirmato)
      else {
        documentUrl = contract.mainDocument.s3FileUrl || null;
      }
    } else {
      // Contratti normali (fornitori): sempre FINAL
      documentUrl = contract.mainDocument.s3FileUrl || null;
    }

    if (documentUrl) {
      this.mainDocumentUrl.set(documentUrl);
    }
  }

  private loadAttoDiNomina(contractId: string): void {
    this.contractRegistryService
      .getAttoDiNomina(contractId)
      .pipe(
        timeout(10000),
        catchError((err) => {
          console.error('Error loading atto di nomina:', err);
          // Don't show error message - atto di nomina might not exist yet
          this.isLoading.set(false);
          return of(null);
        }),
      )
      .subscribe({
        next: (response) => {
          if (response && response.status && response.data) {
            // Ensure responsible object exists to prevent null reference errors
            if (!response.data.responsible) {
              response.data.responsible = {
                responsibleBusinessName: '',
                companyType: '',
                headquarters: '',
                processedDataTypes: [],
                processDescription: '',
                responsibleType: '',
                dataProcessingLocation: '',
                externalSysAdminsText: '',
                responsibleDpoEmail: '',
                transferOutsideEu: false,
                dataDeletionProgram: false,
                criticalSupplierQualification: '',
                usesSubResponsibles: false,
                subResponsibles: []
              } as AttoDiNominaResponsible;
            }
            this.attoDiNomina.set(response.data);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  // Computed getters for easier HTML access
  getContractNumber(): string {
    // Ritorna l'ID alfanumerico dalla route
    const id = this.contractId();
    return id ? `Contratto ${id}` : '';
  }

  getContractInNumber(): string {
    // Ritorna il numero contratto interno (es. CT-2024-001)
    return this.contract()?.generalInfo?.contractInNumber || '';
  }

  getContractSubject(): string {
    return this.contract()?.generalInfo?.contractSubject || '';
  }

  getStatus(): string {
    const rawStatus = this.contract()?.generalInfo?.status || 'DRAFT';
    return this.contractRegistryService.getStatusDisplay(rawStatus);
  }

  getRawStatus(): string {
    return this.contract()?.generalInfo?.status || 'DRAFT';
  }

  // Helper methods to get dropdown labels from values
  getDropdownLabel(options: FormDropdownItem[], value: string | number | null | undefined): string {
    if (!value && value !== 0) return '-';
    // Convert both to string for flexible comparison
    const valueStr = String(value);
    const option = options.find((opt) => String(opt.valueKey) === valueStr);
    if (!option && !this.isContractProfessional()) {
      return this.resolveSupplierDropdownLabel(options, value) ?? valueStr;
    }
    return option?.valueDescription || valueStr;
  }

  getMultiDropdownLabels(
    options: FormDropdownItem[],
    values: (string | number)[] | null | undefined,
  ): string {
    if (!values || values.length === 0) return '-';
    if (!Array.isArray(values) && !this.isContractProfessional()) {
      return this.getDropdownLabel(options, values as string);
    }
    return values.map((val) => this.getDropdownLabel(options, val)).join(', ');
  }

  // Contratti fornitore: tollera chiavi con case/spazi diversi, descrizioni già decodificate
  // e liste serializzate (JSON o separate da virgola) prima di ricadere sul valore grezzo.
  private resolveSupplierDropdownLabel(options: FormDropdownItem[], value: unknown): string | null {
    if (Array.isArray(value)) {
      return value.length > 0
        ? value.map((item) => this.getDropdownLabel(options, item as string)).join(', ')
        : null;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    const option = this.findSupplierDropdownOption(options, raw);
    if (option) return option.valueDescription || raw;

    if (raw.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return this.resolveSupplierDropdownLabel(options, parsed);
        }
      } catch {
        return null;
      }
    }

    if (/[,;]/.test(raw)) {
      const parts = raw.split(/[,;]/).map((part) => part.trim()).filter(Boolean);
      if (parts.some((part) => this.findSupplierDropdownOption(options, part))) {
        return parts
          .map((part) => this.findSupplierDropdownOption(options, part)?.valueDescription || part)
          .join(', ');
      }
    }

    return null;
  }

  private findSupplierDropdownOption(
    options: FormDropdownItem[],
    raw: string,
  ): FormDropdownItem | undefined {
    const normalized = raw.trim().toUpperCase();
    return (
      options.find((opt) => String(opt.valueKey ?? '').trim().toUpperCase() === normalized) ??
      options.find((opt) => (opt.valueDescription ?? '').trim().toUpperCase() === normalized)
    );
  }

  getDropdownText(
    options: FormDropdownItem[],
    value: string | string[] | null | undefined,
  ): string {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) {
      return value.map((item) => this.getDropdownLabel(options, item)).join(', ');
    }
    const exactOption = options.find((option) => option.valueKey === value.trim());
    if (exactOption) return exactOption.valueDescription;

    if (value.trim().startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(value);
        if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
          return this.getDropdownText(options, parsed);
        }
      } catch {
        return value;
      }
    }

    return value.split(/([,;]\s*)/).map((part) => {
      const option = options.find((item) => item.valueKey === part.trim());
      if (!option && !this.isContractProfessional() && part.trim()) {
        return this.findSupplierDropdownOption(options, part)?.valueDescription ?? part;
      }
      return option?.valueDescription ?? part;
    }).join('');
  }

  private normalizeInvolvedEntities(
    value: ContractInvolvedEntity[] | string[] | null | undefined,
  ): ContractInvolvedEntity[] {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value.map((item) => {
      if (typeof item === 'string') {
        return {
          entityCode: item,
          signatoryName: '',
          signatoryRole: '',
        };
      }

      return {
        entityCode: item?.entityCode || '',
        signatoryName: item?.signatoryName || '',
        signatoryRole: item?.signatoryRole || '',
      };
    });
  }

  getInvolvedEntities(): ContractInvolvedEntity[] {
    return this.contract()?.generalInfo?.involvedEntities || [];
  }

  getAvailableControllerEntityOptions(currentIndex: number): AxaEntity[] {
    const involvedEntityCodes = new Set(
      this.getInvolvedEntities()
        .map((entity) => entity.entityCode)
        .filter((value): value is string => !!value),
    );
    const allOptions = involvedEntityCodes.size > 0
      ? this.entityOptions().filter((option) => involvedEntityCodes.has(option.entityCode))
      : this.entityOptions();
    const selectedEntities = (this.attoDiNomina()?.controllers || [])
      .map((controller, index) => (index === currentIndex ? null : controller.controllerEntity))
      .filter((value): value is string => !!value);

    return allOptions.filter((option) => !selectedEntities.includes(option.entityCode));
  }

  getAvailableInvolvedEntityOptions(currentIndex: number): AxaEntity[] {
    const selectedEntities = this.getInvolvedEntities()
      .map((item, index) => (index === currentIndex ? null : item?.entityCode))
      .filter((value) => value !== null && value !== '');

    return this.entityOptions().filter((option) => !selectedEntities.includes(option.entityCode));
  }

  canAddMoreInvolvedEntities(): boolean {
    return this.getInvolvedEntities().length < this.entityOptions().length;
  }

  addInvolvedEntity(): void {
    const currentContract = this.contract();
    if (!currentContract?.generalInfo || !this.canAddMoreInvolvedEntities()) {
      return;
    }

    this.contract.set({
      ...currentContract,
      generalInfo: {
        ...currentContract.generalInfo,
        involvedEntities: [
          ...this.getInvolvedEntities(),
          { entityCode: '', signatoryName: '', signatoryRole: '' },
        ],
      },
    });
  }

  removeInvolvedEntity(index: number): void {
    const currentContract = this.contract();
    if (!currentContract?.generalInfo || this.getInvolvedEntities().length <= 1) {
      return;
    }

    this.contract.set({
      ...currentContract,
      generalInfo: {
        ...currentContract.generalInfo,
        involvedEntities: this.getInvolvedEntities().filter((_, itemIndex) => itemIndex !== index),
      },
    });
  }

  getInvolvedEntityLabel(entityCode: string | null | undefined): string {
    return this.entityOptions().find((entity) => entity.entityCode === entityCode)?.entityDescription || entityCode || '-';
  }

  onInvolvedEntityChange(index: number): void {
    const currentContract = this.contract();
    if (!currentContract?.generalInfo) {
      return;
    }

    const selected = this.getInvolvedEntities()[index];
    const selectedEntity = this.entityOptions().find(
      (entity) => entity.entityCode === selected?.entityCode,
    );
    const involvedEntities = this.getInvolvedEntities().map((entity, entityIndex) =>
      entityIndex === index
        ? {
            ...entity,
            signatoryName: selectedEntity?.signatoryName || '',
            signatoryRole: selectedEntity?.signatoryRole || '',
          }
        : entity,
    );

    this.contract.set({
      ...currentContract,
      generalInfo: {
        ...currentContract.generalInfo,
        involvedEntities,
      },
    });
  }

  // Alias for countriesOptions to match form component naming
  luogoTrattamentoOptions(): FormDropdownItem[] {
    return this.countriesOptions();
  }

  // Helper to parse ISO date string to Date object for datepicker
  private parseDate(dateStr: string | null | undefined): Date | null {
    return this.contractRegistryService.parseContractDate(dateStr);
  }

  // Helper to format date for display
  formatDate(date: Date | string | null | undefined): string {
    const dateObj = this.contractRegistryService.parseContractDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return '-';
    return dateObj.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private formatDateForApi(date: Date | string | null | undefined): string | null {
    return this.contractRegistryService.formatContractDateForApi(date);
  }

  private ensureContractSections(contract: ContractResponse): ContractResponse {
    const normalizedContract = contract as ContractResponse & {
      generalInfo?: ContractResponse['generalInfo'];
      frameworkAgreement?: ContractResponse['frameworkAgreement'];
      economics?: ContractResponse['economics'];
      administration?: ContractResponse['administration'];
      dataProcessing?: ContractResponse['dataProcessing'];
    };

    normalizedContract.generalInfo = {
      supplierId: null,
      professionalId: null,
      contractType: null,
      serviceType: null,
      contractSubject: null,
      outsourcedActivity: null,
      slaKpiPerson: null,
      startDate: null,
      endDate: null,
      automaticRenewal: null,
      terminationNoticeDays: null,
      renewalNoticeDays: null,
      contractSectionStatus: 'DRAFT',
      status: 'DRAFT',
      involvedEntities: [],
      synergies: false,
      synergiesCompanies: null,
      involvedSc: null,
      supplierType: null,
      managementTools: null,
      network: [],
      contractTemplateId: null,
      contractDocumentsNote: null,
      contractInNumber: null,
      ...(normalizedContract.generalInfo as Partial<ContractGeneralInfo> | undefined),
    };

    normalizedContract.frameworkAgreement = {
      frameworkAgreement: false,
      frameworkStartDate: null,
      frameworkEndDate: null,
      automaticRenewal: null,
      renewalNoticeDays: null,
      ...(normalizedContract.frameworkAgreement as Partial<ContractFrameworkAgreement> | undefined),
    };

    normalizedContract.economics = {
      expectedFee: false,
      rebates: false,
      rebatesNote: null,
      invoiceOnSol: false,
      technicalAccount: false,
      allocationMatrix: null,
      expenseAccount: null,
      currency: null,
      totalContractValue: null,
      annualValue: null,
      invoiceFrequency: null,
      ...(normalizedContract.economics as Partial<ContractEconomics> | undefined),
    };

    normalizedContract.administration = {
      managingArea: null,
      claimsAreaReference: null,
      eventualReferent: null,
      buyer: null,
      involvedStructures: [],
      invoiceRecipientEmail: null,
      controlsOwner: null,
      paymentAuthorizer: null,
      documentationAtIP: false,
      supplierSignatory: null,
      supplierSignatoryRole: null,
      professionalSignatory: null,
      professionalSignatoryRole: null,
      ...(normalizedContract.administration as Partial<ContractAdministration> | undefined),
    };

    normalizedContract.dataProcessing = {
      vrfUseCase: null,
      gdprDataProcessing: false,
      privacyResponsibleFunction: null,
      dataTransferOutsideSee: false,
      ...(normalizedContract.dataProcessing as Partial<ContractDataProcessing> | undefined),
    };

    return normalizedContract as ContractResponse;
  }

  private normalizeContractForView(contract: ContractResponse): ContractResponse {
    const normalizedContract = this.ensureContractSections(
      this.cloneValue(contract) as ContractResponse,
    );

    if (normalizedContract.generalInfo?.startDate) {
      (normalizedContract.generalInfo as any).startDate = this.parseDate(
        normalizedContract.generalInfo.startDate,
      );
    }

    if (normalizedContract.generalInfo?.endDate) {
      (normalizedContract.generalInfo as any).endDate = this.parseDate(
        normalizedContract.generalInfo.endDate,
      );
    }

    if (normalizedContract.generalInfo) {
      if (normalizedContract.generalInfo.professionalId) {
        normalizedContract.generalInfo.numberOfRenewals ??= 5;
      }
      normalizedContract.generalInfo.involvedEntities = this.normalizeInvolvedEntities(
        normalizedContract.generalInfo.involvedEntities,
      );
    }

    if (normalizedContract.frameworkAgreement?.frameworkStartDate) {
      (normalizedContract.frameworkAgreement as any).frameworkStartDate = this.parseDate(
        normalizedContract.frameworkAgreement.frameworkStartDate,
      );
    }

    if (normalizedContract.frameworkAgreement?.frameworkEndDate) {
      (normalizedContract.frameworkAgreement as any).frameworkEndDate = this.parseDate(
        normalizedContract.frameworkAgreement.frameworkEndDate,
      );
    }

    return normalizedContract;
  }

  isNumberOfRenewalsRequired(): boolean {
    return this.isContractProfessional() && this.contract()?.generalInfo?.automaticRenewal === 'YES';
  }

  isNumberOfRenewalsInvalid(): boolean {
    const value = this.contract()?.generalInfo?.numberOfRenewals;
    return this.isNumberOfRenewalsRequired() && (value == null || !Number.isInteger(value) || value <= 1);
  }

  private buildContractUpdatePayload(): ContractRequest | null {
    const currentContract = this.contract();
    if (!currentContract) {
      return null;
    }

    if (this.isNumberOfRenewalsInvalid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Numero rinnovi: inserisci un numero intero superiore a 1.',
        life: 3000,
      });
      return null;
    }

    const involvedEntities = this.getInvolvedEntities();
    const selectedEntityCodes = involvedEntities
      .map((entity) => entity.entityCode?.trim())
      .filter((entityCode): entityCode is string => !!entityCode);

    if (selectedEntityCodes.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Seleziona almeno una entity coinvolta prima di salvare.',
        life: 3000,
      });
      return null;
    }

    if (new Set(selectedEntityCodes).size !== selectedEntityCodes.length) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Le entity coinvolte non possono essere duplicate.',
        life: 3000,
      });
      return null;
    }

    return {
      generalInfo: {
        ...currentContract.generalInfo,
        numberOfRenewals: this.isNumberOfRenewalsRequired() ? currentContract.generalInfo.numberOfRenewals : null,
        startDate: this.formatDateForApi(currentContract.generalInfo.startDate),
        endDate: this.formatDateForApi(currentContract.generalInfo.endDate),
        involvedEntities: involvedEntities.map((entity) => ({
          entityCode: entity.entityCode?.trim() || '',
          signatoryName: entity.signatoryName?.trim() || '',
          signatoryRole: entity.signatoryRole?.trim() || '',
        })),
      },
      frameworkAgreement: {
        ...currentContract.frameworkAgreement,
        frameworkStartDate: this.formatDateForApi(currentContract.frameworkAgreement?.frameworkStartDate),
        frameworkEndDate: this.formatDateForApi(currentContract.frameworkAgreement?.frameworkEndDate),
      },
      economics: {
        ...currentContract.economics,
      },
      administration: {
        ...currentContract.administration,
      },
      dataProcessing: {
        ...currentContract.dataProcessing,
      },
    };
  }

  private clearContractSectionBackups(): void {
    this.generalInfoBackup = null;
    this.frameworkAgreementBackup = null;
    this.economicsBackup = null;
    this.dataProcessingBackup = null;
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    return this.contractRegistryService.getStatusSeverity(status);
  }

  toggleEditSection(
    section: 'generali' | 'quadro' | 'economics' | 'amministrazione' | 'vrf',
  ): void {
    switch (section) {
      case 'generali':
        this.isEditModeGenerali.set(!this.isEditModeGenerali());
        break;
      case 'quadro':
        this.isEditModeQuadro.set(!this.isEditModeQuadro());
        break;
      case 'economics':
        this.isEditModeEconomics.set(!this.isEditModeEconomics());
        break;
      case 'amministrazione':
        this.isEditModeAmministrazione.set(!this.isEditModeAmministrazione());
        break;
      case 'vrf':
        this.isEditModeVRF.set(!this.isEditModeVRF());
        break;
    }
  }

  // Individual toggle methods for each section
  toggleEditGenerali(): void {
    if (!this.isEditModeGenerali()) {
      this.generalInfoBackup = this.cloneValue(this.contract()?.generalInfo ?? null);
      this.isEditModeGenerali.set(true);
      return;
    }

    if (this.generalInfoBackup && this.contract()) {
      this.contract.set({
        ...this.contract()!,
        generalInfo: this.generalInfoBackup,
      });
    }

    this.generalInfoBackup = null;
    this.isEditModeGenerali.set(false);
  }

  toggleEditQuadro(): void {
    if (!this.isEditModeQuadro()) {
      this.frameworkAgreementBackup = this.cloneValue(this.contract()?.frameworkAgreement ?? null);
      this.isEditModeQuadro.set(true);
      return;
    }

    if (this.frameworkAgreementBackup && this.contract()) {
      this.contract.set({
        ...this.contract()!,
        frameworkAgreement: this.frameworkAgreementBackup,
      });
    }

    this.frameworkAgreementBackup = null;
    this.isEditModeQuadro.set(false);
  }

  toggleEditEconomics(): void {
    if (!this.isEditModeEconomics()) {
      this.economicsBackup = this.cloneValue(this.contract()?.economics ?? null);
      this.isEditModeEconomics.set(true);
      return;
    }

    if (this.economicsBackup && this.contract()) {
      this.contract.set({
        ...this.contract()!,
        economics: this.economicsBackup,
      });
    }

    this.economicsBackup = null;
    this.isEditModeEconomics.set(false);
  }

  toggleEditAmministrazione(): void {
    if (!this.isEditModeAmministrazione()) {
      // Entrando in edit mode, salva backup
      this.administrationBackup = JSON.parse(JSON.stringify(this.contract()?.administration));
      this.isEditModeAmministrazione.set(true);
    } else {
      // Uscendo da edit mode (annulla), ripristina backup
      if (this.administrationBackup && this.contract()) {
        const updatedContract = {
          ...this.contract()!,
          administration: this.administrationBackup,
        };
        this.contract.set(updatedContract as ContractResponse);
      }
      this.isEditModeAmministrazione.set(false);
    }
  }

  toggleEditVRF(): void {
    if (!this.isEditModeVRF()) {
      this.dataProcessingBackup = this.cloneValue(this.contract()?.dataProcessing ?? null);
      this.isEditModeVRF.set(true);
      return;
    }

    if (this.dataProcessingBackup && this.contract()) {
      this.contract.set({
        ...this.contract()!,
        dataProcessing: this.dataProcessingBackup,
      });
    }

    this.dataProcessingBackup = null;
    this.isEditModeVRF.set(false);
  }

  toggleEditTemplates(): void {
    if (!this.isEditModeTemplates()) {
      // Entrando in edit mode, salva backup
      this.templatesBackup = {
        contractTemplateId: this.contract()?.generalInfo?.contractTemplateId || null,
        attoDiNominaTemplateId: this.attoDiNomina()?.attoDiNominaTemplateId || null,
      };
      this.isEditModeTemplates.set(true);
    } else {
      // Uscendo da edit mode (annulla), ripristina backup
      if (this.templatesBackup && this.contract()) {
        const updatedContract = {
          ...this.contract()!,
          generalInfo: {
            ...this.contract()!.generalInfo!,
            contractTemplateId: this.templatesBackup.contractTemplateId,
          },
        };
        this.contract.set(updatedContract as ContractResponse);

        if (this.attoDiNomina()) {
          const updatedAttoNomina = {
            ...this.attoDiNomina()!,
            attoDiNominaTemplateId: this.templatesBackup.attoDiNominaTemplateId,
          };
          this.attoDiNomina.set(updatedAttoNomina as AttoDiNominaResponse);
        }
      }
      this.isEditModeTemplates.set(false);
    }
  }

  /**
   * Handler per il cambio del template atto di nomina
   */
  onAttoDiNominaTemplateChange(templateId: number | null): void {
    const currentAttoNomina = this.attoDiNomina();
    if (currentAttoNomina) {
      const updatedAttoNomina = {
        ...currentAttoNomina,
        attoDiNominaTemplateId: templateId,
      };
      this.attoDiNomina.set(updatedAttoNomina as AttoDiNominaResponse);
    }
  }

  /**
   * Handler per il cambio del template contratto
   */
  onContractTemplateChange(templateId: number | null): void {
    const currentContract = this.contract();
    if (currentContract) {
      const updatedContract = {
        ...currentContract,
        generalInfo: {
          ...currentContract.generalInfo!,
          contractTemplateId: templateId,
        },
      };
      this.contract.set(updatedContract as ContractResponse);
    }
  }

  // ========== DIALOG TEMPLATE METHODS ==========

  openTemplateContrattoDialog(): void {
    this.showTemplateContrattoDialog.set(true);
  }

  closeTemplateContrattoDialog(): void {
    this.showTemplateContrattoDialog.set(false);
  }

  selectTemplateContratto(template: any): void {
    const templateData = {
      label: template.name,
      value: template.id,
    };

    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(templateData.label)}</strong> come Template Contratto?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        this.onContractTemplateChange(templateData.value);
        this.closeTemplateContrattoDialog();

        this.messageService.add({
          severity: 'success',
          summary: 'Template selezionato',
          detail: `${templateData.label} è stato selezionato come Template Contratto`,
          life: 3000,
        });
      },
    });
  }

  clearTemplateContratto(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il Template Contratto selezionato?',
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.onContractTemplateChange(null);

        this.messageService.add({
          severity: 'info',
          summary: 'Template rimosso',
          detail: 'Il Template Contratto è stato rimosso',
          life: 3000,
        });
      },
    });
  }

  openTemplateAttoNominaDialog(): void {
    this.showTemplateAttoNominaDialog.set(true);
  }

  closeTemplateAttoNominaDialog(): void {
    this.showTemplateAttoNominaDialog.set(false);
  }

  selectTemplateAttoNomina(template: any): void {
    const templateData = {
      label: template.name,
      value: template.id,
    };

    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(templateData.label)}</strong> come Template Atto di Nomina?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        this.onAttoDiNominaTemplateChange(templateData.value);
        this.closeTemplateAttoNominaDialog();

        this.messageService.add({
          severity: 'success',
          summary: 'Template selezionato',
          detail: `${templateData.label} è stato selezionato come Template Atto di Nomina`,
          life: 3000,
        });
      },
    });
  }

  clearTemplateAttoNomina(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il Template Atto di Nomina selezionato?',
      header: 'Conferma rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì, rimuovi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.onAttoDiNominaTemplateChange(null);

        this.messageService.add({
          severity: 'info',
          summary: 'Template rimosso',
          detail: 'Il Template Atto di Nomina è stato rimosso',
          life: 3000,
        });
      },
    });
  }

  // Atto di Nomina - metodi toggle per sotto-sezioni
  toggleEditTitolari(): void {
    if (!this.isEditModeTitolari()) {
      // Entrando in edit mode, salva backup
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeTitolari.set(true);
    } else {
      // Uscendo da edit mode (annulla), ripristina backup
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeTitolari.set(false);
    }
  }

  onControllerEntityChange(controller: { controllerEntity: string; controllerDpoEmail: string }): void {
    const selectedEntity = this.entityOptions().find(
      (entity) => entity.entityCode === controller.controllerEntity,
    );
    controller.controllerDpoEmail = selectedEntity?.dpoEmail || '';
  }

  toggleEditResponsabile(): void {
    if (!this.isEditModeResponsabile()) {
      // Entrando in edit mode, salva backup
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeResponsabile.set(true);
    } else {
      // Uscendo da edit mode (annulla), ripristina backup
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeResponsabile.set(false);
    }
  }

  // ==================== TOGGLE EDIT METHODS - ATTO DI NOMINA ====================

  toggleEditOperazioniTrattamento(): void {
    if (!this.isEditModeOperazioniTrattamento()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeOperazioniTrattamento.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeOperazioniTrattamento.set(false);
    }
  }

  toggleEditFinalitaTrattamento(): void {
    if (!this.isEditModeFinalitaTrattamento()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeFinalitaTrattamento.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeFinalitaTrattamento.set(false);
    }
  }

  toggleEditCategorieSoggetti(): void {
    if (!this.isEditModeCategorieSoggetti()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeCategorieSoggetti.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeCategorieSoggetti.set(false);
    }
  }

  toggleEditDestinatari(): void {
    if (!this.isEditModeDestinatari()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeDestinatari.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeDestinatari.set(false);
    }
  }

  toggleEditConservazioneCancellazione(): void {
    if (!this.isEditModeConservazioneCancellazione()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeConservazioneCancellazione.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeConservazioneCancellazione.set(false);
    }
  }

  toggleEditStrumentiUtilizzati(): void {
    if (!this.isEditModeStrumentiUtilizzati()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeStrumentiUtilizzati.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeStrumentiUtilizzati.set(false);
    }
  }

  toggleEditMisureSicurezza(): void {
    if (!this.isEditModeMisureSicurezza()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeMisureSicurezza.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeMisureSicurezza.set(false);
    }
  }

  toggleEditAmministratoriSistema(): void {
    if (!this.isEditModeAmministratoriSistema()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.adminsTextValidationError.set(null);
      this.isEditModeAmministratoriSistema.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.adminsTextValidationError.set(null);
      this.isEditModeAmministratoriSistema.set(false);
    }
  }

  toggleEditInformazioniCloud(): void {
    if (!this.isEditModeInformazioniCloud()) {
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeInformazioniCloud.set(true);
    } else {
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeInformazioniCloud.set(false);
    }
  }

  // Sub-Responsabili management
  addSubResponsible(): void {
    // TODO: Aprire dialog per selezionare sub-responsabile dal registro
    // Per ora aggiungo un sub-responsabile vuoto
    const newSubResponsible: SubResponsible = {
      businessName: '',
      headquarters: '',
      email: '',
      dataProcessingLocation: 'UE',
      dataProcessingCountry: '',
      nonEuSafeguard: '',
      dataTypes: [],
      delegatedProcessesDescription: '',
      notes: '',
    };

    const currentAttoDiNomina = this.attoDiNomina();
    if (!currentAttoDiNomina) return;

    const currentSubResponsibles = currentAttoDiNomina.responsible?.subResponsibles || [];
    const updatedAttoDiNomina: AttoDiNominaResponse = {
      ...currentAttoDiNomina,
      responsible: {
        ...currentAttoDiNomina.responsible,
        subResponsibles: [...currentSubResponsibles, newSubResponsible],
      },
    };

    this.attoDiNomina.set(updatedAttoDiNomina);

    this.messageService.add({
      severity: 'info',
      summary: 'Sub-Responsabile aggiunto',
      detail: 'Seleziona il sub-responsabile dal registro per compilare i dati',
      life: 3000,
    });
  }

  removeSubResponsible(index: number): void {
    const currentAttoDiNomina = this.attoDiNomina();
    if (!currentAttoDiNomina) return;

    const currentSubResponsibles = currentAttoDiNomina.responsible?.subResponsibles || [];
    const updatedSubResponsibles = currentSubResponsibles.filter((_, i) => i !== index);

    const updatedAttoDiNomina: AttoDiNominaResponse = {
      ...currentAttoDiNomina,
      responsible: {
        ...currentAttoDiNomina.responsible,
        subResponsibles: updatedSubResponsibles,
      },
    };

    this.attoDiNomina.set(updatedAttoDiNomina);

    this.messageService.add({
      severity: 'success',
      summary: 'Eliminato',
      detail: `Sub-Responsabile #${index + 1} eliminato`,
      life: 3000,
    });
  }

  selectSubResponsibleFromRegistry(index: number): void {
    this.selectedSubResponsibleIndex = index;
    this.loadSubResponsiblesForDialog();
    this.showSubResponsibleDialog.set(true);
  }

  selectResponsibleFromRegistry(): void {
    this.loadResponsiblesForDialog();
    this.showResponsibleDialog.set(true);
  }

  // Carica responsabili per dialog
  // NON carica se il contratto è per un PROFESSIONAL (solo per SUPPLIER)
  private loadResponsiblesForDialog(): void {
    // Skip loading if contract is for a professional (not a supplier)
    if (this.isContractProfessional()) {
      console.log('[Contract Detail] Skipping responsibles load - PROFESSIONAL contract');
      return;
    }
    
    this.loadingResponsibles.set(true);
    this.responsibleRegistryService
      .getResponsibles()
      .pipe(
        timeout(10000),
        map((response) => {
          const dataArray = this.responsibleRegistryService.extractResponsiblesArray(response);
          return this.responsibleRegistryService.mapResponsiblesForDisplay(dataArray);
        }),
        catchError((err) => {
          console.error('Error loading responsibles:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei responsabili ha impiegato troppo tempo'
              : 'Impossibile caricare i responsabili dal registro',
          });
          return of([]);
        }),
      )
      .subscribe((tableData) => {
        this.responsibleTableData.set(tableData);
        this.loadingResponsibles.set(false);
      });
  }

  // Carica sub-responsabili per dialog
  // NON carica se il contratto è per un PROFESSIONAL (solo per SUPPLIER)
  private loadSubResponsiblesForDialog(): void {
    // Skip loading if contract is for a professional (not a supplier)
    if (this.isContractProfessional()) {
      console.log('[Contract Detail] Skipping sub-responsibles load - PROFESSIONAL contract');
      return;
    }
    
    this.loadingSubResponsibles.set(true);
    this.subResponsibleRegistryService
      .getSubResponsibles()
      .pipe(
        timeout(10000),
        map((response) => {
          const dataArray =
            this.subResponsibleRegistryService.extractSubResponsiblesArray(response);
          return this.subResponsibleRegistryService.mapSubResponsiblesForDisplay(dataArray);
        }),
        catchError((err) => {
          console.error('Error loading sub-responsibles:', err);
          const isTimeout = err instanceof TimeoutError;
          this.messageService.add({
            severity: 'error',
            summary: isTimeout ? 'Timeout' : 'Errore',
            detail: isTimeout
              ? 'Il caricamento dei sub-responsabili ha impiegato troppo tempo'
              : 'Impossibile caricare i sub-responsabili dal registro',
          });
          return of([]);
        }),
      )
      .subscribe((tableData) => {
        this.subResponsibleTableData.set(tableData);
        this.loadingSubResponsibles.set(false);
      });
  }

  // Gestisce selezione responsabile dal dialog
  onResponsibleSelect(responsible: any): void {
    const responsibleDisplayName = this.getResponsibleDisplayName(responsible);

    this.confirmDialogService.confirm({
      message: `Vuoi impostare <strong>${escapeHtml(responsibleDisplayName)}</strong> come Responsabile del Trattamento?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, imposta',
      rejectLabel: 'Annulla',
      accept: () => {
        const currentAttoDiNomina = this.attoDiNomina();
        if (!currentAttoDiNomina) return;

        const updatedAttoDiNomina: AttoDiNominaResponse = {
          ...currentAttoDiNomina,
          responsible: {
            ...currentAttoDiNomina.responsible,
            responsibleId: responsible.id,
            responsibleBusinessName: responsibleDisplayName,
            companyType: responsible.companyType || '',
            headquarters: responsible.headquarters,
            responsibleDpoEmail: responsible.email || '',
          },
        };

        this.attoDiNomina.set(updatedAttoDiNomina);
        this.showResponsibleDialog.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Responsabile impostato',
          detail: `${responsibleDisplayName} è stato impostato come Responsabile del Trattamento`,
          life: 3000,
        });
      },
    });
  }

  private getResponsibleDisplayName(responsible: any): string {
    return this.responsibleRegistryService.formatResponsibleBusinessName(responsible);
  }

  // Gestisce selezione sub-responsabile dal dialog
  onSubResponsibleSelect(subResponsible: any): void {
    if (this.selectedSubResponsibleIndex === null) return;

    this.confirmDialogService.confirm({
      message: `Vuoi impostare <strong>${escapeHtml(subResponsible.businessName)}</strong> come Sub-Responsabile #${this.selectedSubResponsibleIndex + 1}?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, imposta',
      rejectLabel: 'Annulla',
      accept: () => {
        const currentAttoDiNomina = this.attoDiNomina();
        if (!currentAttoDiNomina) return;

        const currentSubResponsibles = [
          ...(currentAttoDiNomina.responsible?.subResponsibles || []),
        ];
        if (
          this.selectedSubResponsibleIndex !== null &&
          this.selectedSubResponsibleIndex < currentSubResponsibles.length
        ) {
          // Aggiorna il sub-responsabile mantenendo gli altri campi già compilati
          currentSubResponsibles[this.selectedSubResponsibleIndex] = {
            ...currentSubResponsibles[this.selectedSubResponsibleIndex],
            subResponsibleId: subResponsible.id,
            businessName: subResponsible.businessName,
            companyType: subResponsible.companyType || '',
            headquarters: subResponsible.headquarters,
            email: subResponsible.email,
          };

          const updatedAttoDiNomina: AttoDiNominaResponse = {
            ...currentAttoDiNomina,
            responsible: {
              ...currentAttoDiNomina.responsible,
              subResponsibles: currentSubResponsibles,
            },
          };

          this.attoDiNomina.set(updatedAttoDiNomina);
          this.showSubResponsibleDialog.set(false);

          this.messageService.add({
            severity: 'success',
            summary: 'Sub-Responsabile impostato',
            detail: `${subResponsible.businessName} è stato impostato`,
            life: 3000,
          });
        }
      },
    });
  }

  toggleEditSubResponsabili(): void {
    if (!this.isEditModeSubResponsabili()) {
      // Entrando in edit mode, salva backup
      this.attoDiNominaBackup = JSON.parse(JSON.stringify(this.attoDiNomina()));
      this.isEditModeSubResponsabili.set(true);
    } else {
      // Uscendo da edit mode (annulla), ripristina backup
      if (this.attoDiNominaBackup) {
        this.attoDiNomina.set(this.attoDiNominaBackup);
      }
      this.isEditModeSubResponsabili.set(false);
    }
  }

  // Save methods for Atto di Nomina sections
  saveTitolari(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Titolari salvati con successo',
          life: 3000,
        });
        this.isEditModeTitolari.set(false);
      },
      error: (error) => {
        console.error('Error saving titolari:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio dei titolari',
          life: 3000,
        });
      },
    });
  }

  saveResponsabile(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Responsabile salvato con successo',
          life: 3000,
        });
        this.isEditModeResponsabile.set(false);
        this.attoDiNominaBackup = null; // Clear backup after save
      },
      error: (error) => {
        console.error('Error saving responsabile:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio del responsabile',
          life: 3000,
        });
      },
    });
  }

  /**
   * Controlla se ci sono campi Amministrazione con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsInAdministrationFields(): string[] {
    const fieldsWithErrors: string[] = [];
    const administration = this.contract()?.administration;

    if (!administration) return [];

    const strictPattern = /^[^<>"'&/\\]*$/;

    const textFields = [
      { value: administration.eventualReferent, label: 'Referente Eventuale' },
      { value: administration.buyer, label: 'Buyer' },
      { value: administration.supplierSignatory, label: 'Firmatario Fornitore' },
      { value: administration.supplierSignatoryRole, label: 'Ruolo Firmatario Fornitore' },
      { value: administration.controlsOwner, label: 'Responsabile Controlli' },
      { value: administration.paymentAuthorizer, label: 'Autorizzatore Pagamenti' },
      { value: administration.invoiceRecipientEmail, label: 'Email Destinatario Fatture' },
    ];

    textFields.forEach(field => {
      if (field.value && !strictPattern.test(field.value)) {
        fieldsWithErrors.push(field.label);
      }
    });

    return fieldsWithErrors;
  }

  /**
   * Salva le modifiche alla sezione Amministrazione
   * Utilizza API PATCH specifica che funziona anche per contratti ACTIVE
   */
  saveAmministrazione(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali nei campi testo
    const specialCharsFields = this.hasSpecialCharsInAdministrationFields();
    if (specialCharsFields.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    const contractId = this.contractId();
    const administration = this.contract()?.administration;

    if (!contractId || !administration) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    // Prepara il payload per l'API
    const payload = {
      managingArea: administration.managingArea,
      claimsAreaReference: administration.claimsAreaReference,
      eventualReferent: administration.eventualReferent,
      buyer: administration.buyer,
      involvedStructures: administration.involvedStructures || [],
      invoiceRecipientEmail: administration.invoiceRecipientEmail,
      documentationAtIP: administration.documentationAtIP,
      supplierSignatory: administration.supplierSignatory,
      supplierSignatoryRole: administration.supplierSignatoryRole,
      controlsOwner: administration.controlsOwner,
      paymentAuthorizer: administration.paymentAuthorizer,
    };

    this.contractRegistryService.updateAdministration(contractId, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Amministrazione salvata con successo',
          life: 3000,
        });
        this.isEditModeAmministrazione.set(false);
        this.administrationBackup = null; // Clear backup after save
      },
      error: (error: any) => {
        console.error('Error saving administration:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || "Errore durante il salvataggio dell'amministrazione",
          life: 3000,
        });
      },
    });
  }

  /**
   * Carica le colonne delle tabelle per i dialog template
   */
  private loadTemplateColumns(): void {
    const configContratto = TABLE_CONFIG['elencoTemplateContratti'];
    if (configContratto) {
      this.templateContrattoColumns = configContratto.columns;
    }

    const configAttoNomina = TABLE_CONFIG['elencoTemplateAttiNomina'];
    if (configAttoNomina) {
      this.templateAttoNominaColumns = configAttoNomina.columns;
    }
  }

  /**
   * Carica le opzioni dei template contratto e atto di nomina dal catalogo
   * v2 DocumentAsset (`/api/v2/document-templates`). Il valore option è il
   * `businessKey` del DocumentAsset (VARCHAR) coerente con il campo persistito
   * `contract_template_id` / `atto_di_nomina_template_id`.
   */
  private loadTemplateOptions(): void {
    const domainType = this.getTemplateDomainType();

    // Carica template contratto (MASTER_TEMPLATE con subDomainType=CONTRACTS)
    this.documentTemplateV2Service
      .listMasterTemplates(domainType, 'CONTRACTS')
      .subscribe({
        next: (assets: DocumentAssetSummary[]) => {
          const selectable = assets.filter(isSelectableTemplate);
          const options = selectable.map((t) => ({
            label: t.name || t.businessKey,
            value: t.businessKey,
          }));
          this.templateContrattoOptions.set(options);
          this.templateContrattoTableData.set(
            selectable.map(toTemplateSelectionRow),
          );
        },
        error: (error) => {
          console.error('Error loading contract templates (v2):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare i template contratto',
            life: 3000,
          });
        },
      });

    // Carica template atto di nomina (MASTER_TEMPLATE con subDomainType=ATTO_DI_NOMINA)
    this.documentTemplateV2Service
      .listMasterTemplates(domainType, 'ATTO_DI_NOMINA')
      .subscribe({
        next: (assets: DocumentAssetSummary[]) => {
          const selectable = assets.filter(isSelectableTemplate);
          const options = selectable.map((t) => ({
            label: t.name || t.businessKey,
            value: t.businessKey,
          }));
          this.templateAttoNominaOptions.set(options);
          this.templateAttoNominaTableData.set(
            selectable.map(toTemplateSelectionRow),
          );
        },
        error: (error) => {
          console.error('Error loading atto di nomina templates (v2):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare i template atto di nomina',
            life: 3000,
          });
        },
      });
  }

  /**
   * Ritorna il `domainType` DocumentAsset ('SUPPLIERS' | 'PROFESSIONALS')
   * corrispondente al contesto di navigazione corrente.
   */
  private getTemplateDomainType(): string {
    return this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
  }

  /**
   * Salva le modifiche ai template (contratto e atto di nomina)
   * Disponibile solo per contratti in stato GENERATED
   */
  saveTemplates(): void {
    const contractId = this.contractId();
    const contract = this.contract();

    if (!contractId || !contract) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    // Verifica che il contratto sia in stato GENERATED
    if (contract.generalInfo?.status !== 'GENERATED') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'I template possono essere modificati solo per contratti in stato GENERATED',
        life: 3000,
      });
      return;
    }

    // Validazione obbligatoria: il template contratto deve essere sempre selezionato
    if (!contract.generalInfo?.contractTemplateId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di Validazione',
        detail: 'Il Template Contratto è obbligatorio',
        life: 4000,
      });
      return;
    }

    // Validazione: se GDPR è abilitato, anche il template Atto di Nomina è obbligatorio
    const attoNomina = this.attoDiNomina();
    if (contract.dataProcessing?.gdprDataProcessing && !attoNomina?.attoDiNominaTemplateId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di Validazione',
        detail: 'Il Template Atto di Nomina è obbligatorio quando il trattamento GDPR è abilitato',
        life: 4000,
      });
      return;
    }

    // Prepara il payload con i nuovi template IDs e la nota documenti (mantenuta)
    const payload = {
      contractTemplateId: contract.generalInfo?.contractTemplateId || null,
      attoDiNominaTemplateId: attoNomina?.attoDiNominaTemplateId || null,
      contractDocumentsNote: contract.documentsRequest?.contractDocumentsNote || null,
    };

    // Usa l'API updateContractDocuments per salvare entrambi i template
    this.contractRegistryService.updateContractDocuments(contractId, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail:
            'Template aggiornati con successo. Usa "Rigenera Contratto" per applicare le modifiche.',
          life: 4000,
        });
        this.isEditModeTemplates.set(false);
        this.templatesBackup = null;
        // Ricarica i dettagli del contratto per ottenere i dati aggiornati
        this.loadContractDetails(contractId, true);
      },
      error: (error: any) => {
        console.error('Error saving templates:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il salvataggio dei template',
          life: 3000,
        });
      },
    });
  }

  saveSubResponsabili(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Sub-responsabili salvati con successo',
          life: 3000,
        });
        this.isEditModeSubResponsabili.set(false);
        this.attoDiNominaBackup = null; // Clear backup after save
      },
      error: (error) => {
        console.error('Error saving sub-responsabili:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio dei sub-responsabili',
          life: 3000,
        });
      },
    });
  }

  // ==================== SAVE METHODS - ATTO DI NOMINA (nuove sezioni) ====================

  saveOperazioniTrattamento(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Operazioni di trattamento salvate con successo',
          life: 3000,
        });
        this.isEditModeOperazioniTrattamento.set(false);
      },
      error: (error) => {
        console.error('Error saving operazioni trattamento:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle operazioni di trattamento',
          life: 3000,
        });
      },
    });
  }

  saveFinalitaTrattamento(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Finalità del trattamento salvate con successo',
          life: 3000,
        });
        this.isEditModeFinalitaTrattamento.set(false);
      },
      error: (error) => {
        console.error('Error saving finalità trattamento:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle finalità del trattamento',
          life: 3000,
        });
      },
    });
  }

  saveCategorieSoggetti(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Categorie di soggetti salvate con successo',
          life: 3000,
        });
        this.isEditModeCategorieSoggetti.set(false);
      },
      error: (error) => {
        console.error('Error saving categorie soggetti:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle categorie di soggetti',
          life: 3000,
        });
      },
    });
  }

  saveDestinatari(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Destinatari salvati con successo',
          life: 3000,
        });
        this.isEditModeDestinatari.set(false);
      },
      error: (error) => {
        console.error('Error saving destinatari:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio dei destinatari',
          life: 3000,
        });
      },
    });
  }

  saveConservazioneCancellazione(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Conservazione e cancellazione salvate con successo',
          life: 3000,
        });
        this.isEditModeConservazioneCancellazione.set(false);
      },
      error: (error) => {
        console.error('Error saving conservazione:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio dei dati di conservazione',
          life: 3000,
        });
      },
    });
  }

  saveStrumentiUtilizzati(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Strumenti utilizzati salvati con successo',
          life: 3000,
        });
        this.isEditModeStrumentiUtilizzati.set(false);
      },
      error: (error) => {
        console.error('Error saving strumenti utilizzati:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio degli strumenti utilizzati',
          life: 3000,
        });
      },
    });
  }

  saveMisureSicurezza(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Misure di sicurezza salvate con successo',
          life: 3000,
        });
        this.isEditModeMisureSicurezza.set(false);
      },
      error: (error) => {
        console.error('Error saving misure sicurezza:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle misure di sicurezza',
          life: 3000,
        });
      },
    });
  }

  saveAmministratoriSistema(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    if (!this.hasMeaningfulText(this.attoDiNomina()!.adminsText)) {
      this.adminsTextValidationError.set('Campo obbligatorio');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Elenco amministratori di sistema è obbligatorio',
        life: 3000,
      });
      return;
    }

    this.adminsTextValidationError.set(null);

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Amministratori di sistema salvati con successo',
          life: 3000,
        });
        this.isEditModeAmministratoriSistema.set(false);
      },
      error: (error) => {
        console.error('Error saving amministratori sistema:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio degli amministratori di sistema',
          life: 3000,
        });
      },
    });
  }

  saveInformazioniCloud(): void {
    const contractId = this.contractId();
    if (!contractId || !this.attoDiNomina()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare: dati non validi',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.updateAttoDiNomina(contractId, this.attoDiNomina()).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Informazioni sul cloud salvate con successo',
          life: 3000,
        });
        this.isEditModeInformazioniCloud.set(false);
      },
      error: (error) => {
        console.error('Error saving informazioni cloud:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle informazioni sul cloud',
          life: 3000,
        });
      },
    });
  }

  saveSection(section: 'generali' | 'quadro' | 'economics' | 'amministrazione' | 'vrf'): void {
    if (section === 'generali' && this.isContractProfessional() && !this.contract()?.generalInfo?.network?.length) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Seleziona almeno un network.',
        life: 3000,
      });
      return;
    }

    const contractId = this.contractId();
    const payload = this.buildContractUpdatePayload();

    if (!contractId || !payload) {
      if (!contractId) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'ID contratto non disponibile.',
          life: 3000,
        });
      }
      return;
    }

    this.contractRegistryService.updateContract(contractId, payload).subscribe({
      next: (response) => {
        this.contract.set(this.normalizeContractForView(response));
        this.clearContractSectionBackups();
        this.toggleEditSection(section);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Modifiche salvate con successo',
          life: 3000,
        });
      },
      error: (error) => {
        console.error(`Error saving ${section} section:`, error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio delle modifiche',
          life: 3000,
        });
      },
    });
  }

  goBack(): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`]);
  }

  // Document actions
  /**
   * Scarica un allegato del contratto
   */
  onDownloadDocument(doc: RelatedDocument): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService
      .downloadAttachment(contractId, doc.documentId.toString())
      .subscribe({
        next: (blob: Blob) => {
          // Crea un URL temporaneo dal Blob
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = addSolCodeToFileName(doc.fileName, this.professionalData()?.solCode);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: 'Download completato',
            detail: `"${doc.fileName}" scaricato con successo`,
            life: 3000,
          });
        },
        error: (error: any) => {
          console.error('Error downloading document:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante il download del documento',
            life: 5000,
          });
        },
      });
  }
  /**
   * Elimina un allegato del contratto
   */
  onDeleteDocument(doc: RelatedDocument): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    this.confirmDialogService.confirm({
      message: `Sei sicuro di voler eliminare "${escapeHtml(doc.fileName)}"?`,
      header: 'Conferma eliminazione',
      acceptLabel: 'Sì, elimina',
      rejectLabel: 'Annulla',
      accept: () => {
        this.contractRegistryService
          .deleteAttachment(contractId, doc.documentId.toString())
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Documento eliminato',
                detail: `"${doc.fileName}" eliminato con successo`,
                life: 3000,
              });

              // Ricarica la lista degli attachments
              this.loadAttachments(contractId);
            },
            error: (error) => {
              console.error('Error deleting document:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: error?.error?.message || "Errore durante l'eliminazione del documento",
                life: 5000,
              });
            },
          });
      },
    });
  }

  /**
   * Carica gli attachments del contratto
   */
  private loadAttachments(contractId: string): void {
    this.contractRegistryService.getAttachments(contractId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Mappa attachments a RelatedDocument
          const documents: RelatedDocument[] = response.data.map((att) => ({
            documentId: att.id,
            fileName: att.fileName,
            uploadDate: att.uploadedAt,
            uploadedBy: att.uploadedBy,
            title: att.description,
            documentS3: att.s3FileUrl,
          }));
          this.documents.set(documents);
        }
      },
      error: (error) => {
        console.error('Error loading attachments:', error);
      },
    });
  }

  /** Upload documento contrattuale (evento dal componente documents-manager). */
  async onContractDocumentUpload(event: { file: File; title: string }): Promise<void> {
    const { file, title } = event;
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    // Validazione completa (size, MIME, estensione, magic numbers) — PDF/Word/Excel/PowerPoint
    const validation = await this.fileValidationService.validateAttachment(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000,
      });
      return;
    }

    this.isUploadingAttachment.set(true);
    this.contractRegistryService.uploadContractAttachment(contractId, file, title).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Documento caricato',
          detail: `"${title}" caricato con successo`,
          life: 3000,
        });
        this.loadAttachments(contractId);
        this.isUploadingAttachment.set(false);
      },
      error: (error) => {
        console.error('Error uploading attachment:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il caricamento del documento',
          life: 5000,
        });
        this.isUploadingAttachment.set(false);
      },
    });
  }

  /** Download documento contrattuale (evento dal componente documents-manager). */
  onContractDocumentDownload(item: DocManagerItem): void {
    this.onDownloadDocument(item.raw as RelatedDocument);
  }

  /** Eliminazione documento contrattuale (evento dal componente documents-manager). */
  onContractDocumentRemove(item: DocManagerItem): void {
    this.onDeleteDocument(item.raw as RelatedDocument);
  }

  // ==================== MAIN DOCUMENT MANAGEMENT ====================

  /**
   * Gestisce l'upload del documento principale del contratto
   * Utilizza FileValidationService per validazione completa (size, MIME, extension, magic numbers)
   */
  async onMainDocumentSelect(event: any): Promise<void> {
    const file = event.files[0];
    if (!file) {
      return;
    }

    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      event.files = [];
      return;
    }

    // Validazione completa del file con FileValidationService
    // Include: size, MIME type, extension, magic numbers (file signature)
    const validation = await this.fileValidationService.validateDocument(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000,
      });

      // Log dettagli per debugging (solo in sviluppo)
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }

      event.files = [];
      return;
    }

    this.isUploadingMainDocument.set(true);
    this.uploadErroredMainDoc.set(false);

    // Determina il documentType in base allo step professionale:
    // - Step 1 (isInUploadStep): UNSIGNED (documento da firmare)
    // - Step 3 (canCountersignDocument): FINAL (documento controfirmato)
    // - Default: FINAL (contratti normali)
    const documentType = this.isInUploadStep() 
      ? DocumentType.UNSIGNED 
      : this.canCountersignDocument() 
      ? DocumentType.FINAL 
      : DocumentType.FINAL;

    this.contractRegistryService.uploadMainDocument(contractId, file, 'system', documentType).subscribe({
      next: (response) => {
        this.mainDocumentUrl.set(response.data);
        this.uploadErroredMainDoc.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Documento principale caricato con successo',
          life: 3000,
        });
        this.isUploadingMainDocument.set(false);
        event.files = [];
        // Resetta il fileupload dopo il successo
        if (this.mainDocUpload) {
          this.mainDocUpload.clear();
        }
        
        // Ricarica i dati del contratto per aggiornare mainDocument con il nuovo s3FileUrl
        this.loadContractDetails(contractId, true);
        // Se stava sostituendo il doc controfirmato, resetta il flag
        this.isReplacingFinalDoc.set(false);
      },
      error: (error) => {
        console.error('Error uploading main document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il caricamento del documento',
          life: 5000,
        });
        this.isUploadingMainDocument.set(false);
        this.uploadErroredMainDoc.set(true);
        this.mainDocumentUrl.set(null);
        if (this.mainDocUpload) {
          this.mainDocUpload.clear();
        }
      },
    });
  }

  retryMainDocumentUpload(): void {
    this.uploadErroredMainDoc.set(false);
    this.messageService.add({
      severity: 'info',
      summary: 'Pronto per nuovo caricamento',
      detail: 'Seleziona nuovamente il documento.',
      life: 3000,
    });
  }

  /** Abilita la sostituzione del documento controfirmato già caricato (step 3) */
  startReplaceCountersignedDoc(): void {
    this.isReplacingFinalDoc.set(true);
  }

  /**
   * Scarica il documento principale del contratto
   */
  downloadMainDocument(): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    if (!this.mainDocumentUrl()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessun documento principale disponibile',
        life: 3000,
      });
      return;
    }

    // Determina il documentType in base allo step professionale:
    // - Step 2 (isWaitingForFiduciarySignature): UNSIGNED (documento inviato al fiduciario)
    // - Step 3 (canCountersignDocument): EXTERNALLY_SIGNED (documento firmato dal fiduciario)
    // - Default: FINAL (contratti normali o documento finale)
    const documentType = this.isWaitingForFiduciarySignature() 
      ? DocumentType.UNSIGNED 
      : this.canCountersignDocument() 
      ? DocumentType.EXTERNALLY_SIGNED 
      : DocumentType.FINAL;

    this.contractRegistryService.downloadMainDocument(contractId, documentType).subscribe({
      next: (blob: Blob) => {
        // Estrai il nome del file dall'URL S3 se disponibile
        const url = this.mainDocumentUrl();
        let fileName = 'contratto-principale.pdf';
        if (url) {
          const urlParts = url.split('/');
          fileName = urlParts[urlParts.length - 1] || fileName;
        }
        fileName = addSolCodeToFileName(fileName, this.professionalData()?.solCode);

        // Crea un URL temporaneo dal Blob
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: 'Documento principale scaricato con successo',
          life: 3000,
        });
      },
      error: (error: any) => {
        console.error('Error downloading main document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il download del documento',
          life: 5000,
        });
      },
    });
  }

  /**
   * Elimina il documento principale del contratto
   */
  removeMainDocument(): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    this.confirmDialogService.confirm({
      message: 'Sei sicuro di voler eliminare il documento principale del contratto?',
      header: 'Conferma eliminazione',
      acceptLabel: 'Sì, elimina',
      rejectLabel: 'Annulla',
      accept: () => {
        this.contractRegistryService.deleteMainDocument(contractId).subscribe({
          next: () => {
            this.mainDocumentUrl.set(null);
            this.messageService.add({
              severity: 'success',
              summary: 'Documento eliminato',
              detail: 'Documento principale eliminato con successo',
              life: 3000,
            });
          },
          error: (error: any) => {
            console.error('Error deleting main document:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || "Errore durante l'eliminazione del documento",
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Attiva il contratto passando dallo stato GENERATED ad ACTIVE
   * Richiede che il documento principale sia stato caricato
   */
  activateContract(): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    if (!this.mainDocumentUrl()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Carica il documento principale prima di attivare il contratto',
        life: 4000,
      });
      return;
    }

    this.confirmDialogService.confirm({
      message:
        'Sei sicuro di voler attivare il contratto? Una volta attivato, non sarà più possibile modificare le informazioni principali.',
      header: 'Conferma attivazione contratto',
      acceptLabel: 'Sì, attiva',
      rejectLabel: 'Annulla',
      accept: () => {
        this.contractRegistryService.activateContract(contractId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Contratto attivato',
              detail: 'Il contratto è stato attivato con successo',
              life: 4000,
            });
            // Ricarica i dettagli del contratto per aggiornare lo stato
            this.loadContractDetails(contractId, true);
          },
          error: (error: any) => {
            console.error('Error activating contract:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || "Errore durante l'attivazione del contratto",
              life: 5000,
            });
          },
        });
      },
    });
  }

  // ==================== PROFESSIONAL WORKFLOW METHODS ====================

  /**
   * Invia il contratto al fiduciario per la firma
   * Completa lo step CONTRACT_SENT → crea CONTRACT_SIGNATURE
   */
  sendToFiduciaryForSignature(): void {
    const profId = this.professionalId();
    const contractId = this.contractId();
    
    if (!profId || !contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Dati fiduciario non disponibili',
        life: 3000,
      });
      return;
    }

    if (!this.mainDocumentUrl()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Carica il documento principale prima di inviarlo al fiduciario',
        life: 4000,
      });
      return;
    }

    this.confirmDialogService.confirm({
      message: 'Sei sicuro di voler inviare il contratto al fiduciario per la firma? Il fiduciario riceverà una notifica email con le istruzioni per firmare il documento.',
      header: 'Conferma invio al fiduciario',
      acceptLabel: 'Sì, invia',
      rejectLabel: 'Annulla',
      accept: () => {
        this.professionalsService.updateRegistrationStep(profId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Contratto inviato',
              detail: 'Il contratto è stato inviato al fiduciario per la firma',
              life: 4000,
            });
            // Ricarica i dettagli per aggiornare professionalStep
            this.loadContractDetails(contractId, true);
          },
          error: (error: any) => {
            console.error('Error sending contract to fiduciary:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || "Errore durante l'invio del contratto",
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Scarica il documento firmato dal fiduciario (main document EXTERNALLY_SIGNED)
   */
  downloadSignedByFiduciary(): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    // Verifica che ci sia l'URL del documento EXTERNALLY_SIGNED
    const contract = this.contract();
    const externallySigned = contract?.mainDocument?.s3FileUrlExternallySigned;
    
    if (!externallySigned) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Documento non disponibile',
        detail: 'Il documento firmato dal fiduciario non è ancora stato caricato dal fiduciario',
        life: 5000,
      });
      return;
    }

    // Scarica il documento EXTERNALLY_SIGNED
    this.contractRegistryService.downloadMainDocument(contractId, DocumentType.EXTERNALLY_SIGNED).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = addSolCodeToFileName(
          'contratto-firmato-fiduciario.pdf',
          this.professionalData()?.solCode,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: 'Documento firmato dal fiduciario scaricato con successo',
          life: 3000,
        });
      },
      error: (error: any) => {
        console.error('Error downloading signed document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il download del documento firmato',
          life: 5000,
        });
      },
    });
  }

  /**
   * Scarica documento UNSIGNED (originale da firmare)
   */
  downloadUnsignedDocument(): void {
    this.downloadDocumentByType(DocumentType.UNSIGNED, 'contratto-originale.pdf', 'Documento originale');
  }

  /**
   * Scarica documento EXTERNALLY_SIGNED (firmato dal professional)
   */
  downloadExternallySignedDocument(): void {
    this.downloadDocumentByType(DocumentType.EXTERNALLY_SIGNED, 'contratto-firmato-professional.pdf', 'Documento firmato dal fiduciario');
  }

  /**
   * Scarica documento FINAL (controfirmato da AXA)
   */
  downloadFinalDocument(): void {
    this.downloadDocumentByType(DocumentType.FINAL, 'contratto-controfirmato.pdf', 'Documento controfirmato');
  }

  /**
   * Metodo generico per scaricare un documento per tipo
   */
  private downloadDocumentByType(documentType: DocumentType, fileName: string, label: string): void {
    const contractId = this.contractId();
    if (!contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto non disponibile',
        life: 3000,
      });
      return;
    }

    this.contractRegistryService.downloadMainDocument(contractId, documentType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = addSolCodeToFileName(fileName, this.professionalData()?.solCode);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: `${label} scaricato con successo`,
          life: 3000,
        });
      },
      error: (error: any) => {
        console.error(`Error downloading ${documentType} document:`, error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || `Errore durante il download del ${label.toLowerCase()}`,
          life: 5000,
        });
      },
    });
  }

  /**
   * Gestisce la selezione del documento controfirmato
   * Valida il file prima di effettuare l'upload
   */
  async onCountersignedDocumentSelect(event: any): Promise<void> {
    const files = event.files || event.currentFiles || event.target?.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    // Validate file
    const validation = await this.fileValidationService.validateDocument(file);

    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File non valido',
        detail: validation.error || 'Il file selezionato non è valido',
        life: 5000,
      });
      return;
    }

    this.selectedCountersignedFile = file;
    this.uploadCountersignedDocumentToServer();
  }

  private selectedCountersignedFile: File | null = null;
  private selectedExternallySignedFile: File | null = null;

  /**
   * Gestisce la selezione del documento firmato dal fiduciario ricevuto via email/PEC
   * Upload durante lo step 2 (isWaitingForFiduciarySignature)
   */
  async onExternallySignedDocumentSelect(event: any): Promise<void> {
    const files = event.files || event.currentFiles || event.target?.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    // Validate file
    const validation = await this.fileValidationService.validateDocument(file);

    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File non valido',
        detail: validation.error || 'Il file selezionato non è valido',
        life: 5000,
      });
      event.files = [];
      return;
    }

    this.selectedExternallySignedFile = file;
    this.uploadExternallySignedDocumentToServer(event);
  }

  /**
   * Upload del documento EXTERNALLY_SIGNED ricevuto via email/PEC
   * Step 2: quando il fiduciario ha firmato fuori dal portale
   */
  private uploadExternallySignedDocumentToServer(event: any): void {
    const contractId = this.contractId();
    const file = this.selectedExternallySignedFile;

    if (!contractId || !file) {
      return;
    }

    this.isUploadingMainDocument.set(true);
    this.uploadErroredMainDoc.set(false);

    // Usa uploadCountersignedDocument che internamente usa EXTERNALLY_SIGNED
    this.contractRegistryService.uploadCountersignedDocument(contractId, file, 'system').subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Documento caricato',
          detail: 'Documento firmato dal fiduciario caricato con successo. Il processo può ora continuare.',
          life: 5000,
        });
        this.isUploadingMainDocument.set(false);
        this.selectedExternallySignedFile = null;
        event.files = [];
        
        // Ricarica per aggiornare mainDocument e far avanzare lo step
        this.loadContractDetails(contractId, true);
      },
      error: (error: any) => {
        console.error('Error uploading externally signed document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante il caricamento del documento firmato',
          life: 5000,
        });
        this.isUploadingMainDocument.set(false);
        this.uploadErroredMainDoc.set(true);
        event.files = [];
      },
    });
  }

  private uploadCountersignedDocumentToServer(): void {
    const contractId = this.contractId();
    const file = this.selectedCountersignedFile;

    if (!contractId || !file) {
      return;
    }

    this.isUploadingMainDocument.set(true);
    this.uploadErroredMainDoc.set(false);

    this.contractRegistryService.uploadCountersignedDocument(contractId, file).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Documento caricato',
          detail: 'Documento controfirmato caricato con successo',
          life: 4000,
        });
        this.isUploadingMainDocument.set(false);
        this.selectedCountersignedFile = null;
        // Ricarica per aggiornare mainDocument
        this.loadContractDetails(contractId, true);
      },
      error: (error: any) => {
        console.error('Error uploading countersigned document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento del documento controfirmato',
          life: 5000,
        });
        this.isUploadingMainDocument.set(false);
        this.uploadErroredMainDoc.set(true);
      },
    });
  }

  /**
   * Attiva il contratto professional finale
   * Completa lo step CONTRACT_COUNTERSIGNATURE → professional diventa ACTIVE
   */
  activateProfessionalContract(): void {
    const profId = this.professionalId();
    const contractId = this.contractId();
    
    if (!profId || !contractId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Dati fiduciario non disponibili',
        life: 3000,
      });
      return;
    }

    if (!this.mainDocumentUrl()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Carica il documento controfirmato prima di attivare il contratto',
        life: 4000,
      });
      return;
    }

    this.confirmDialogService.confirm({
      message: 'Sei sicuro di voler attivare il contratto? Il fiduciario riceverà l\'email di conferma.',
      header: 'Conferma attivazione contratto',
      acceptLabel: 'Sì, attiva',
      rejectLabel: 'Annulla',
      accept: () => {
        // Prima attiva il fiduciario, poi attiva il contratto
        this.professionalsService.updateRegistrationStep(profId).pipe(
          switchMap(() => this.contractRegistryService.activateContract(contractId))
        ).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Contratto attivato',
              detail: 'Il contratto è stato attivato con successo',
              life: 4000,
            });
            // Ricarica i dettagli per aggiornare professionalStep
            this.loadContractDetails(contractId, true);
          },
          error: (error: any) => {
            console.error('Error activating professional contract:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error?.error?.message || "Errore durante l'attivazione del contratto",
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Rigenera il documento del contratto processando il template
   * Solo per contratti in stato GENERATED con un template associato
   */
  regenerateContract(): void {
    const contractId = this.contractId();
    const templateId = this.contract()?.generalInfo?.contractTemplateId;

    if (!contractId || !templateId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'ID contratto o template non disponibile',
        life: 3000,
      });
      return;
    }

    if (!this.canRegenerateContract()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Il contratto deve essere nello stato GENERATED per rigenerare il documento',
        life: 3000,
      });
      return;
    }

    this.isRegeneratingDocument.set(true);

    // Call backend API v2 to render DOCX directly.
    // POST /pal-dmn-documents/api/v2/document-templates/{businessKey}/render-docx
    // Payload: { contextId, contextType: 'CONTRACT', outputFormat: 'DOCX' }
    this.documentTemplateV2Service
      .renderDocx(templateId, {
        contextId: contractId,
        contextType: 'CONTRACT',
        outputFormat: 'DOCX',
      })
      .subscribe({
        next: (blob: Blob) => {
          console.log('✅ Backend v2 render complete, blob size:', blob.size);

          const entityId = this.contract()?.generalInfo?.supplierId
            || this.contract()?.generalInfo?.professionalId
            || 'unknown';
          const filename = `contratto_${entityId}_${new Date().getTime()}.docx`;

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: 'Documento rigenerato',
            detail: 'Il documento del contratto è stato rigenerato con successo',
            life: 4000,
          });

          this.isRegeneratingDocument.set(false);
        },
        error: (error) => {
          console.error('❌ Error rendering template (v2):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante la rigenerazione del documento',
            life: 5000,
          });
          this.isRegeneratingDocument.set(false);
        },
      });
  }

  /**
   * Formatta la dimensione del file in formato leggibile
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  // ========================================
  // CONTRACT ACTIONS MANAGEMENT
  // ========================================

  /**
   * Carica le azioni contrattuali per il contratto corrente
   */
  private loadContractActions(): void {
    const contractId = this.contractId();
    if (!contractId) return;

    this.contractActionService.getActionsByContractId(contractId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.contractActions.set(response.data);
          console.log('✅ Contract actions loaded:', response.data.length);
        }
      },
      error: (err) => {
        console.error('❌ Error loading contract actions:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le azioni del contratto',
          life: 5000,
        });
      },
    });
  }

  /**
   * Naviga alla pagina di creazione nuova azione
   * Blocca la creazione se esiste già un rinnovo attivo
   */
  openNewActionDialog(): void {
    const actions = this.contractActions();
    const activeRenewal = actions.find(
      (action) =>
        action.actionType === 'RENEW_CONTRACT' && action.status === ContractActionStatus.ACTIVE,
    );

    if (activeRenewal) {
      // Blocca la creazione mostrando un messaggio informativo
      this.confirmDialogService.showInfo(
        'Non è possibile creare una nuova azione di rinnovo perché ne esiste già una attiva.',
        'Rinnovo già in corso',
      );
    } else {
      this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}/actions/new`]);
    }
  }

  /**
   * Naviga alla pagina di dettaglio azione (in sola lettura)
   */
  viewActionDetails(action: ContractAction): void {
    // Navigate to dedicated detail page
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}/actions/${action.id}`]);
  }

  /**
   * Naviga alla pagina di modifica azione
   */
  editAction(action: ContractAction): void {
    this.router.navigate([
      `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}/actions/${action.id}/edit`,
    ]);
  }

  /**
   * Completa un'azione (cambia stato a COMPLETED)
   */
  completeAction(action: ContractAction): void {
    if (!action.id) return;

    this.confirmDialogService.confirm({
      header: 'Completa Azione',
      message: `Sei sicuro di voler completare l'azione "${escapeHtml(action.actionName)}"? Una volta completata, non potrà più essere modificata.`,
      accept: () => {
        const contractId = this.contractId();
        if (!contractId) return;

        this.contractActionService.completeAction(contractId, action.id!).subscribe({
          next: (response) => {
            if (response.status && response.data) {
              // Aggiorna la lista locale
              const actions = this.contractActions();
              const index = actions.findIndex((a) => a.id === action.id);
              if (index !== -1) {
                actions[index] = response.data;
                this.contractActions.set([...actions]);
              }
              this.messageService.add({
                severity: 'success',
                summary: 'Azione Completata',
                detail:
                  response.message ||
                  `L'azione "${action.actionName}" è stata completata con successo`,
                life: 3000,
              });
            }
          },
          error: (err) => {
            console.error('❌ Error completing action:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: err.error?.message || "Impossibile completare l'azione",
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Elimina un'azione
   */
  deleteAction(action: ContractAction): void {
    if (!action.id) return;

    this.confirmDialogService.confirm({
      header: 'Elimina Azione',
      message: `Sei sicuro di voler eliminare l'azione "${escapeHtml(action.actionName)}"? Questa operazione non può essere annullata.`,
      accept: () => {
        const contractId = this.contractId();
        if (!contractId) return;

        this.contractActionService.deleteAction(contractId, action.id!).subscribe({
          next: (response) => {
            if (response.status) {
              // Rimuovi dalla lista locale
              const actions = this.contractActions().filter((a) => a.id !== action.id);
              this.contractActions.set(actions);
              this.messageService.add({
                severity: 'success',
                summary: 'Azione Eliminata',
                detail: response.message || `L'azione "${action.actionName}" è stata eliminata`,
                life: 3000,
              });
            }
          },
          error: (err) => {
            console.error('❌ Error deleting action:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: err.error?.message || "Impossibile eliminare l'azione",
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Ottiene il label human-readable del tipo azione
   */
  getActionTypeLabel(actionType: string): string {
    return (
      CONTRACT_ACTION_TYPE_LABELS[actionType as keyof typeof CONTRACT_ACTION_TYPE_LABELS] ||
      actionType
    );
  }

  /**
   * Ottiene il label human-readable dello stato azione
   */
  getActionStatusLabel(status: string): string {
    return (
      CONTRACT_ACTION_STATUS_LABELS[status as keyof typeof CONTRACT_ACTION_STATUS_LABELS] || status
    );
  }

  /**
   * Ottiene la severity PrimeNG per lo stato azione
   */
  getActionStatusSeverity(
    status: string,
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    return getActionStatusSeverity(status);
  }

  /**
   * Genera il documento dell'azione dal template selezionato
   */
  generateActionDocument(action: ContractAction): void {
    if (!action.selectedTemplateId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Nessun template selezionato per questa azione',
        life: 3000,
      });
      return;
    }

    if (!action.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID azione non disponibile',
        life: 3000,
      });
      return;
    }

    // Call backend API v2 to render DOCX directly for the action
    this.documentTemplateV2Service
      .renderDocx(action.selectedTemplateId, {
        contextId: action.id.toString(),
        contextType: 'ACTION',
        outputFormat: 'DOCX',
      })
      .subscribe({
        next: (blob: Blob) => {
          console.log('✅ Backend v2 render complete (action), blob size:', blob.size);

          const actionTypeName = this.getActionTypeLabel(action.actionType)
            .toLowerCase()
            .replace(/\s+/g, '_');
          const filename = `azione_${actionTypeName}_${action.id}_${new Date().getTime()}.docx`;

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: 'Documento generato',
            detail: "Il documento dell'azione è stato generato con successo",
            life: 4000,
          });
        },
        error: (error) => {
          console.error('❌ Error rendering template (v2, action):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: error?.error?.message || 'Errore durante la generazione del documento',
            life: 5000,
          });
        },
      });
  }
}
